import React, { useEffect, useRef, useState, Suspense, lazy, useCallback, useMemo, useImperativeHandle, forwardRef } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import {
  useGLTF, useAnimations, OrbitControls, Caustics, AccumulativeShadows, ContactShadows,
  RandomizedLight, Html, Environment, MeshTransmissionMaterial, Bounds, BakeShadows, SoftShadows, useBounds 
} from '@react-three/drei'
import { Perf } from "r3f-perf"
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';
import './styles.css';  // Add any styles for labels
import { EffectComposer, Bloom, BrightnessContrast, ChromaticAberration, N8AO, ToneMapping } from '@react-three/postprocessing'
import { BlendFunction, ToneMappingMode } from 'postprocessing'
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { LineGeometry, LineMaterial, Line2, RGBELoader } from 'three-stdlib';
import { XR, useXRHitTest, createXRStore } from '@react-three/xr';
import { useSpring, animated } from '@react-spring/three'
import { gsap } from "gsap";
import QRCode from 'qrcode';
import { Spinner } from 'spin.js';
import Modal from 'react-modal';
import { TOUCH } from "three";

import { useShowCorrectCoordinates } from "./functions/useShowCorrectCoordinates";
import { CreateCoordinates } from "./functions/CreateCoordinates";
import exampleSofaDimensions from "./functions/ExampleSofaDimensions.json";
import exampleScrewDimensions from "./functions/ExampleScrewDimensions.json";
import { computeScaledBoundingBox } from './boundingBoxUtils';
//import { SmoothControls } from './components/SmoothControls';

// import all the components
import { ARModel } from "./components/ARModel";
import { Arrowhead } from "./components/ArrowHead";
import { DimensionLine } from "./components/DimensionLine";
import { DimensionLabel } from "./components/DimensionLabel";
import ShadowHandler from "./ShadowHandler";
import { use } from 'react';
import SimpleCoordinates from './functions/SimpleCoordinates';
//import { is } from '@react-three/fiber/dist/declarations/src/core/utils';
//import { mode } from '../../../webpack.config';

// const Canvas = lazy(() => import('@react-three/fiber'));

const SelectedViewer = {
  ThreeSixty: 'ThreeSixtyViewer',
  ThreeD: 'ModelViewer',
  TwoD: 'Lifestyle',
}

function AutoFitCamera({ cameraOffset, modelRef }) {
  const { camera } = useThree(); // Access camera and renderer
  const controlsRef = useRef();

  useEffect(() => {
    if (modelRef.current) {
      console.log("Model ref loaded:", modelRef.current); // Debugging model ref

      // Get the bounding box of the model
      const box = new THREE.Box3().setFromObject(modelRef.current);
      const size = box.getSize(new THREE.Vector3()); // Size of the model
      const center = box.getCenter(new THREE.Vector3()); // Center of the model

      // Debugging: Log the bounding box, size, and center
      console.log("Bounding Box:", box);
      console.log("Model Size:", size);
      console.log("Model Center:", center);

      // Calculate the max dimension (width, height, depth)
      const maxDim = Math.max(size.x, size.y, size.z);
      const fov = camera.fov * (Math.PI / 180); // Convert vertical fov to radians

      console.log("CameraFOV: " + camera.fov);

      // Calculate the distance the camera needs to be from the object to fit it all
      const cameraDistance = maxDim / (2 * Math.tan(fov / 2));

      // Debugging: Log the calculated camera distance
      console.log("Calculated Camera Distance:", cameraDistance);

      // Update the camera position
      camera.position.set(
        -size.z,
        size.y,
        center.z + cameraDistance * cameraOffset
      );

      // Optional: Make sure the controls target the center of the object
      if (controlsRef.current) {
        controlsRef.current.maxDistance = cameraDistance * cameraOffset;
        controlsRef.current.minDistance = cameraDistance;
        controlsRef.current.target.set(center.x, center.y, center.z);
        controlsRef.current.update(); // Required to re-render the scene
      }

      camera.lookAt(center); // Ensure the camera is looking at the model
      camera.updateProjectionMatrix(); // Update the camera projection

      // Debugging: Log the new camera position
      console.log("Updated Camera Position:", camera.position);
    }
  }, [camera, modelRef]);

  return <OrbitControls ref={controlsRef} />;
}

const ThreeScene = React.memo(({
  canvasRef,
  controls,
  params,
  cameraOffset,
  line_width,
  showLabels,
  animationPlaying,
  setAnimationPlaying,
  animationDirection,
  lastAnimationName,
  setLastAnimationName,
  selectedMeshName,
  setSelectedMeshName,
  isFirstAnimToggled,
  isSecondAnimToggled,
  setIsFirstAnimToggled,
  setIsSecondAnimToggled,
  setHasAnimations,
  setAnimationLength,
  setAnimationsLoaded,
  variantFileGlb,
  setGltfScene,
  variantFileUsd,
  variantFilesHdr,
  variantEnvironmentsHdr,
  forceUpdate,
  yawAngle,
  pitchAngle,
  aspectRatio,
  objectDimensions,
  parentWidth,
  parentHeight,
  windowHeight,
  windowWidth,
  sceneBackgroundColour,
  isSmallObject,
  dimensionsOnTop,
  productSettings,
  isMobile,
  configuratorEnabled,
  onMeshClicked,
  setLoadingLightMap,
  viewerModeRef,
  setIsObjectPanning,
  isObjectPanning,
  setEnableInteractionHint,
  enableInteractionHint,
  showInteractionHint,
  isRotationSettled,
  setIsRotationSettled,
  setDisableLabels,
  selectedViewer,
  controlsEnabled,
  orbitControlsEnabled,
  setOrbitControlsEnabled,
  isTouchDevice
}) => {
  const { camera } = useThree();
  
  const [coordinateData, setCoordinateData] = useState(null);
  const [dimensions, setDimensions] = useState();
  const [box, setBox] = useState();
  const [center, setCenter] = useState();
  const [size, setSize] = useState();
  const [isCameraSettled, setIsCameraSettled] = useState(false);
  const [lightMaps, setLightMaps] = useState({});
  const [lines, setLines] = useState([]);
  const [cameraDistance, setCameraDistance] = useState(1);
  const [isCamRotationSettled, setIsCamRotationSettled] = useState(true);
  //const [isRotationSettled, setIsRotationSettled] = useState(true);
  const [dimensionsCalculated, setDimensionsCalculated] = useState(false);
  const [scaledArrowHeight, setScaledArrowHeight] = useState();
  const scaledArrowHeightUpdateRef = useRef(null);
  const scaledArrowHeightTimeoutRef = useRef(null);
  
  // Batched callback for Arrowhead components to reduce state update overhead
  const handleScaledArrowHeightUpdate = useCallback((height) => {
    if (scaledArrowHeightUpdateRef.current === null || 
        Math.abs(scaledArrowHeightUpdateRef.current - height) > 0.001) {
      scaledArrowHeightUpdateRef.current = height;
      
      // Cancel existing animation frame if pending
      if (scaledArrowHeightTimeoutRef.current !== null) {
        cancelAnimationFrame(scaledArrowHeightTimeoutRef.current);
      }
      
      // Batch updates using requestAnimationFrame for better performance
      scaledArrowHeightTimeoutRef.current = requestAnimationFrame(() => {
        setScaledArrowHeight(scaledArrowHeightUpdateRef.current);
        scaledArrowHeightTimeoutRef.current = null;
      });
    }
  }, []);
  
  // Cleanup animation frame on unmount
  useEffect(() => {
    return () => {
      if (scaledArrowHeightTimeoutRef.current !== null) {
        cancelAnimationFrame(scaledArrowHeightTimeoutRef.current);
      }
    };
  }, []);
  
  const [startingYawSet, setStartingYawSet] = useState(false);
  const [animationFinished, setAnimationFinished] = useState(false);
  const [readyToRender, setReadyToRender] = useState(false);
  const [touchStart, setTouchStart] = useState(null);

  // Access camera, scene, and WebGL renderer using useThree hook
  const mixerRef = useRef();
  //const controls = useRef();
  const modelRef = useRef();

  const minArrowHeight = 2.5;
  const namedAnimationSpeed = 1.3;
  const boundsMaxDuration = 0.8

  //If it's pure3D, we need to capture the model from different points of view without showing it. Is that even possible?
  //Or do we just rotate the camera fully and take screenshots each time?
  //Add 360 spin animation anyway?

  useFrame(() => {
    if (Math.abs(cameraDistance - camera.position.distanceTo(new THREE.Vector3(0, 0, 0))) > 0.15) setCameraDistance(camera.position.distanceTo(new THREE.Vector3(0, 0, 0)));
  });

  // Calculate distance-based scale
  const minScale = 0.5;

  const distanceScale = controls.current ? minScale + Math.min(1, (cameraDistance - controls.current.minDistance) * minScale /
    (controls.current.maxDistance - controls.current.minDistance)) : 1;
  //const dimensionsMap = new Map();

  //TODO: If configuratorEnabled, then use default configuration to show specific nodes only
  //console.log('model:', params.modelName);
  console.log('show labels:', showLabels);
  //console.log('distanceScale:', distanceScale);

  const glbUrl = variantFileGlb[0]; // process.env.PUBLIC_URL + "/" + params.modelName; // variantFileGlb[0];
  useGLTF.preload(glbUrl);


  const { scene: gltfScene, animations } = useGLTF(glbUrl, (loader) => {
    loader.setCrossOrigin("anonymous");
    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath(
      "https://cdn.jsdelivr.net/npm/three@0.153.0/examples/jsm/libs/draco/gltf/"
    );
    loader.setDRACOLoader(dracoLoader);
  });


  // Track the last viewerMode we've triggered bounds for
  const lastViewerModeForBoundsRef = useRef(null);
  // Track if we're currently processing a bounds animation for the current viewerMode

  // Trigger bounds refresh when switching viewerMode (360 or 3D) or when model first becomes ready
  // Use a polling approach with ref to avoid re-renders
  useEffect(() => {
    if (!readyToRender) return;
    
    const checkViewerMode = () => {
      const currentViewerMode = viewerModeRef.current;
      if (currentViewerMode === '360' || currentViewerMode === '3D') {
        // Check if viewerMode has actually changed to a different mode
        const viewerModeChanged = lastViewerModeForBoundsRef.current !== currentViewerMode;
        
        // Only trigger bounds if viewerMode has changed to a different mode
        // This ensures we only trigger once per viewerMode transition
        if (viewerModeChanged) {
          console.log(`Triggering bounds refresh for viewerMode: ${currentViewerMode} (changed from ${lastViewerModeForBoundsRef.current})`);
          
          // Update the tracked viewerMode BEFORE triggering bounds
          // This prevents the effect from running again for the same viewerMode
          lastViewerModeForBoundsRef.current = currentViewerMode;
          
        }
      }
    };
    
    // Check immediately
    checkViewerMode();
    
    // Poll for changes (only when ready to render)
    const interval = setInterval(checkViewerMode, 100);
    return () => clearInterval(interval);
  }, [readyToRender]);

  useEffect(() => {
    if (animations) {
      setAnimationLength(animations.length);
      console.log('animations:', animations.length);
    }
    
  }, [animations]);

  useEffect(() => {
    return () => {
      gltfScene.traverse((obj) => {
        if (obj.isMesh) {
          obj.geometry?.dispose();
          const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
          mats.forEach((mat) => {
            for (const key in mat) {
              const value = mat[key];
              if (value?.isTexture) value.dispose();
            }
            mat.dispose();
          });
        }
      });
    };
  }, [gltfScene]);

  useEffect(() => {
    if (!gltfScene) return;
    if (selectedViewer === SelectedViewer.ThreeD) {
      gltfScene.visible = true;
    }
    else {
      gltfScene.visible = false;
    }
  }, [selectedViewer]);


  function cleanMaterial(material) {
    for (const key in material) {
      const value = material[key];
      if (value && typeof value.dispose === 'function') {
        value.dispose();
      }
    }
    material.dispose?.();
  }

  function MemoryLoggerAndCleanup() {
    const { gl: renderer, scene } = useThree();

    // 🔹 Log memory usage periodically
    // useEffect(() => {
    //   const interval = setInterval(() => {
    //     console.log('Memory:', renderer.info.memory);
    //   }, 2000);

    //   return () => clearInterval(interval);
    // }, [renderer]);

    // 🔹 Cleanup when component unmounts
    useEffect(() => {
      return () => {
        if (gltfScene) {
          useGLTF.clear(gltfScene);
          console.log('Cleaning up GLTF scene resources...');
          gltfScene.traverse((child) => {
            if (child.isMesh) {
              if (child.geometry) child.geometry.dispose();
              if (child.material) {
                if (Array.isArray(child.material)) {
                  child.material.forEach(mat => {
                    for (const key in mat) {
                      if (mat[key]?.isTexture) {
                        mat[key].dispose();
                      }
                    }
                    mat.dispose();
                  });
                } else {
                  for (const key in child.material) {
                    if (child.material[key]?.isTexture) {
                      child.material[key].dispose();
                    }
                  }
                  child.material.dispose();
                }
              }
            }
          });
        }
      };
    }, [gltfScene]);

    return null;
  }


  const minAxisIndex = (box) => {
    const boxSize = new THREE.Vector3();
    box.getSize(boxSize);
    return boxSize.x <= boxSize.y && boxSize.x <= boxSize.z
      ? 0
      : boxSize.y <= boxSize.z
        ? 1
        : 2;
  };

  const maxAxisIndex = (box) => {
    const boxSize = new THREE.Vector3();
    box.getSize(boxSize);
    return boxSize.x >= boxSize.y && boxSize.x >= boxSize.z
      ? 0
      : boxSize.y >= boxSize.z
        ? 1
        : 2;
  }

  const [hovered, setHovered] = useState(null);
  //const { scene } = useThree();

  const handlePointerOver = (event) => {

    event.stopPropagation();

    const mesh = event.object;
    if (!mesh) return;

    //console.log('mesh hovered:', mesh);

    // Find the mesh inside gltfScene
    const sceneMesh = findMeshInScene(mesh, gltfScene);
    if (!sceneMesh) {
      console.warn("Mesh not found in gltfScene");
      return;
    }

    const topLevelMesh = getTopLevelMesh(sceneMesh);
    //console.log("Top-level mesh:", topLevelMesh);

    if (productSettings.meshInteractions &&
      (productSettings.interactiveMeshes.length < 1 || !productSettings.interactiveMeshes.includes(sceneMesh.name))) return;

    // Traverse through all children of the top-level mesh
    topLevelMesh.traverse((child) => {
      if (child.isMesh) {
        if (productSettings.meshInteractions && child !== sceneMesh) return;
        console.log('child mesh:', child);
        // Clone material only if not already cloned
        if (!child.userData.originalMaterial) {
          child.userData.originalMaterial = child.material; // Store original
          child.material = child.material.clone(); // Clone for unique highlight
        }

        if (!child.material.emissive) return;
        child.material.emissive.setHex(0x888888); // Apply highlight
      }
    });

    if (productSettings.meshInteractions) {
      setHovered(sceneMesh);
    }
    else {
      setHovered(topLevelMesh);
    }

    // Clone the material if it hasn't been cloned already
    if (!mesh.userData.originalMaterial) {
      mesh.userData.originalMaterial = mesh.material; // Store original material
      mesh.material = mesh.material.clone(); // Clone the material
    }

    setHovered(mesh);
    mesh.material.emissive.setHex(0xffffff); // Glow effect
  };

  const handlePointerOut = (event) => {
    event.stopPropagation();

    const mesh = event.object;
    if (!mesh) return;

    // Find the mesh inside gltfScene
    const sceneMesh = findMeshInScene(mesh, gltfScene);
    if (!sceneMesh) return;

    if (productSettings.meshInteractions &&
      (productSettings.interactiveMeshes.length < 1 || !productSettings.interactiveMeshes.includes(sceneMesh.name))) return;

    // Get the top-level mesh
    const topLevelMesh = getTopLevelMesh(sceneMesh);

    // Restore materials for all children
    topLevelMesh.traverse((child) => {
      if (child.isMesh && child.userData.originalMaterial) {
        child.material = child.userData.originalMaterial; // Restore original
        delete child.userData.originalMaterial;
      }
    });

    setHovered(null);
  };

  // useEffect(() => {
  //   if (
  //     gltfScene &&
  //     productSettings.meshInteractions &&
  //     Array.isArray(productSettings.interactiveMeshes) &&
  //     productSettings.interactiveMeshes.length > 0
  //   ) {
  //     const timer = setTimeout(() => {
  //       flashInteractiveMeshes();
  //     }, 2000);
  //     return () => clearTimeout(timer);
  //   }
  // }, [gltfScene, productSettings.meshInteractions, productSettings.interactiveMeshes]);

  const flashInteractiveMeshes = () => {
    if (!productSettings.animationMapping) return;
    Object.keys(productSettings.animationMapping).forEach((meshName, index) => {
      setTimeout(() => {
        console.log('Flashing mesh:', meshName);
        const mesh = gltfScene.getObjectByName(meshName);
        console.log('Found mesh:', mesh);
        if (mesh) {
          if (!mesh.userData.originalMaterial) {
            mesh.userData.originalMaterial = mesh.material; // Store original
            mesh.material = mesh.material.clone(); // Clone for unique highlight
          }
          let flashing = true;
          let startTime = Date.now();

          // Store original emissive color if not already stored
          if (!mesh.userData.originalEmissive) {
            mesh.userData.originalEmissive = mesh.material.emissive ? mesh.material.emissive.clone() : new THREE.Color(0x000000);
          }

          const flash = () => {
            if (!flashing) return;
            const elapsed = (Date.now() - startTime) / 1000;
            if (elapsed >= 3) {
              // Restore original emissive color
              if (mesh.material.emissive && mesh.userData.originalEmissive) {
                mesh.material.emissive.copy(mesh.userData.originalEmissive);
              }
              flashing = false;
              return;
            }
            if (mesh.material.emissive) {
              // Sine wave between 0 and 1
              const intensity = 0.5 + 0.5 * Math.sin(elapsed * 8); // 8 = speed
              // Blend between original and highlight color
              const highlight = new THREE.Color(0x888888);
              mesh.material.emissive.copy(mesh.userData.originalEmissive.clone().lerp(highlight, intensity));
            }
            requestAnimationFrame(flash);
          };

          flash();

        }
      }, 1500);
    });
  }
  //Use productSettings.animationMapping, go through each key
  //Find the mesh in the scene, highlight it for a second, then move to the next one

  const getTopLevelMesh = (mesh) => {
    while (mesh.parent && mesh.parent.name !== "Studio") {
      mesh = mesh.parent; // Move up the hierarchy
    }
    return mesh; // Return the top-level mesh with 'Scene' as parent
  };

  const findMeshInScene = (mesh, scene) => {
    let foundMesh = null;
    scene.traverse((child) => {
      if (child === mesh) {
        foundMesh = child;
      }
    });

    return foundMesh;
  };

  const zoomOut = (sceneMesh) => {
    //Find the top-level mesh (with 'Scene' as parent)
    const topLevelMesh = getTopLevelMesh(sceneMesh);

    // Compute the bounding box of the clicked mesh
    const box = computeScaledBoundingBox(topLevelMesh);
    const center = new THREE.Vector3();
    const size = new THREE.Vector3();
    box.getCenter(center);
    box.getSize(size);

    // Calculate a larger zoom distance based on the diagonal size
    const boundingDiagonal = size.length();
    const targetZoom = boundingDiagonal * params.maxZoomFactor;

    console.log("Zooming to:", center, "Zoom:", targetZoom);

    // Animate the camera position and zoom
    if (controls.current && camera) {
      // Increase maxDistance
      controls.current.maxDistance = targetZoom;

      // Direction from camera to target (keep orientation)
      const dir = new THREE.Vector3()
        .subVectors(camera.position, controls.current.target)
        .normalize();

      // New camera position = center + direction * distance
      const newCamPos = new THREE.Vector3()
        .copy(center)
        .addScaledVector(dir, targetZoom);

      gsap.to(camera.position, {
        duration: 1,
        x: newCamPos.x,
        y: newCamPos.y,
        z: newCamPos.z,
        ease: "power2.out",
        onUpdate: () => controls.current.update(),
      });

      // gsap.to(camera.position, {
      //   duration: 1,
      //   x: center.x,
      //   y: center.y + size.y / 2,
      //   z: center.z + targetZoom,
      //   ease: "power2.out",
      //   onUpdate: () => controls.current.update(),
      // });

      gsap.to(controls.current.target, {
        duration: 1,
        x: center.x,
        y: center.y,
        z: center.z,
        ease: "power2.out",
        onUpdate: () => controls.current.update()
      });
    }
  }

  const zoomToMesh = (sceneMesh) => {
    // Find the top-level mesh (with 'Scene' as parent)
    const topLevelMesh = getTopLevelMesh(sceneMesh);
    //console.log('Top-level mesh:', topLevelMesh);

    // Compute the bounding box of the clicked mesh
    const box = computeScaledBoundingBox(topLevelMesh);
    const center = new THREE.Vector3();
    const size = new THREE.Vector3();
    box.getCenter(center);
    box.getSize(size);

    // Calculate zoom factor based on the mesh size
    // const sizeFactor = size.length() * params.zoomMultiplier;
    // const minZoom = Math.min(Math.max(sizeFactor, 0.1) * params.zoomClose, params.maxZoomClose);
    // const maxZoom = size.length() * params.maxZoomFactor;
    const targetZoom =
      size.length() * params.maxZoomFactor * params.zoomOnPartMultiplier;

    console.log("Zooming to:", center, "Zoom:", targetZoom);

    //TODO: Use the same to set the camera to a viewpoint or transition between spin and full control mode
    // Animate the camera position and zoom
    if (controls.current && camera) {
      gsap.to(camera.position, {
        duration: 1,
        x: center.x,
        y: center.y,
        z: center.z + targetZoom, // Adjust Z to move closer
        ease: "power2.out",
        onUpdate: () => controls.current.update(),
      });

      gsap.to(controls.current.target, {
        duration: 1,
        x: center.x,
        y: center.y,
        z: center.z,
        ease: "power2.out",
        onUpdate: () => controls.current.update(),
      });
    }

    if (onMeshClicked) {
      onMeshClicked(topLevelMesh.name);
    }
  }

  const handleClick = (event) => {
    event.stopPropagation();
    const mesh = event.object;
    //console.log("mesh clicked:", mesh);
    if (!mesh) return;

    // Find the mesh inside gltfScene
    const sceneMesh = findMeshInScene(mesh, gltfScene);
    if (!sceneMesh) {
      console.warn("Mesh not found in gltfScene");
      return;
    }
    if (productSettings.meshInteractions &&
      (productSettings.interactiveMeshes.length < 1 || !productSettings.interactiveMeshes.includes(sceneMesh.name))) return;

    if (productSettings.meshInteractions) {
      if (onMeshClicked) {
        console.log('onMeshClicked for mesh:', sceneMesh.name);
        setSelectedMeshName(sceneMesh.name);
        onMeshClicked(sceneMesh.name);
      }
    }
    else {
      zoomToMesh(sceneMesh);
    }
  };


  const findHdrMatchingMeshName = (meshName, gltfScene) => {
    // Extract the keyword from meshName
    const match = meshName.match(/_([^_]+)\.hdr$/);
    if (!match) {
      console.warn("Invalid variant file format:", meshName);
      return null;
    }
    const keyword = match[1]; // Extracted keyword
    console.log("Extracted Keyword:", keyword);
    return keyword;
  };


  //console.log('GLTF scene:', gltfScene);
  // Call useAnimations unconditionally, providing a fallback empty array if animations is null or undefined
  const { actions, mixer } = useAnimations(animations || [], gltfScene);

  // // Then, set actions and mixer to null if animations is missing
  const animationActions = animations ? actions : null;
  const animationMixer = animations ? mixer : null;

  const GetObjectExtDimensions = (box) => {
    const positions = {
      backBottomLeft: new THREE.Vector3(box.min.x, box.min.y, box.min.z),
      frontBottomLeft: new THREE.Vector3(box.min.x, box.min.y, box.max.z),
      backBottomRight: new THREE.Vector3(box.max.x, box.min.y, box.min.z),
      frontBottomRight: new THREE.Vector3(box.max.x, box.min.y, box.max.z),
      topLeft: new THREE.Vector3(
        box.min.x,
        box.max.y,
        box.min.z
      ),
    };
    // console.log('first arrow start, end:', positions.backBottomLeft, positions.frontBottomLeft2);
    // console.log('second arrow start, end:', positions.frontBottomLeft1, positions.frontBottomRight1);
    // console.log('third arrow start, end:', positions.backBottomLeft, positions.topLeft);

    console.log('positions, box:', positions, box);
    const toCoordStr = (v) => `${v.x.toFixed(4)}, ${v.y.toFixed(4)}, ${v.z.toFixed(4)}`;

    const arrowPositioning = [
      {
        start: toCoordStr(positions.frontBottomLeft),
        end: toCoordStr(positions.backBottomLeft),
        offset: 0,
        "offsetDirection": "x",
      },
      {
        start: toCoordStr(positions.frontBottomRight),
        end: toCoordStr(positions.frontBottomLeft),
        offset: 0,
        "offsetDirection": "y",
      },
      {
        start: toCoordStr(positions.topLeft),
        end: toCoordStr(positions.backBottomLeft),
        offset: 0,
        "offsetDirection": "z",
      },
    ];

    return {
      modelSetup: {
        units: productSettings.units || "cm",
        labelOffset: 6,
        origin: "center",
      },
      arrowPositioning,
    };
  };

  const playNamedAnimation = (name, direction = 1) => {
    const action = actions[name];
    action.reset();
    // Set the animation to play only once:
    action.loop = THREE.LoopOnce;
    action.clampWhenFinished = true;
    action.time = direction === namedAnimationSpeed ? 0 : actions[name].getClip().duration;
    action.timeScale = direction;
    action.play();
    // Finished handler scoped to this action
    const onFinished = (e) => {
      if (e.action !== action) return; // only respond to *this* animation
      setAnimationPlaying(false);
      setAnimationFinished(true);
      setDisableLabels(false);
      action.getMixer().removeEventListener('finished', onFinished); // remove only once
    };

    action.getMixer().addEventListener('finished', onFinished);
  }

  const playNewAnimation = (selectedMeshName, animName, action, mapping) => {
    // Restore materials for all children to remove highlight
    // gltfScene.traverse((child) => {
    //   if (child.isMesh && child.userData.originalMaterial) {
    //     child.material = child.userData.originalMaterial; // Restore original
    //     delete child.userData.originalMaterial;
    //   }
    // });

    if (!productSettings.reversableAnimation) {
      if (selectedMeshName) {
        if (animationPlaying) {
          console.log('anim name from mapping:', animName, mapping, selectedMeshName);
          //Play animName in actions if it exists
          if (animName && actions[animName]) {
            actions[animName].reset();
            actions[animName].enabled = true;
            actions[animName].loop = THREE.LoopOnce; // Play once for non-reversible animation
            //play actions[animName ]
            actions[animName].play();
          }
        } else {
          actions[animName]?.stop(); // Stop the animation
        }
        //TODO: This logic isn't right, needs updating as we shouldn't loop a reversable animation
      } else if (animationPlaying) {
        action.loop = THREE.LoopRepeat; // default looping for non-reversible animation
        action.play(); // Play the first animation
      } else {
        action.stop(); // Stop the animation
      }
    } else if (animationPlaying && animName && actions[animName]) {
      playNamedAnimation(animName, animationDirection * namedAnimationSpeed);
    } else if (animationPlaying) {
      action.reset();
      action.enabled = true;
      // Set the animation to play only once:
      action.setLoop(THREE.LoopOnce, 1);
      action.clampWhenFinished = true;
      action.time = animationDirection === 1 ? 0 : action.getClip().duration;
      console.log('clip duration:', action.getClip().duration);
      action.timeScale = animationDirection;
      action.play();

      // Listen for finished event to reset animationPlaying
      const onFinished = () => {
        setAnimationPlaying(false);
        setAnimationFinished(true);
        setDisableLabels(false);
        action._mixer.removeEventListener('finished', onFinished);
      };
      action._mixer.addEventListener('finished', onFinished);
    }
    if (animName) { setLastAnimationName(animName); }
    else { setLastAnimationName(null); }
  }

  // Check for animations and set hasAnimations accordingly
  useEffect(() => {
    if (animations && animations.length > 0) {
      console.log("Animations found:", animations); // Debugging
      setHasAnimations(true); // Update state to indicate animations are available
    } else {
      console.log("No animations found"); // Debugging
      setHasAnimations(false); // No animations available
    }
    if (gltfScene) setAnimationsLoaded(true);
  }, [animations]);

  useEffect(() => {
    console.log('variant files hdr:', variantFilesHdr);
    if (!variantFilesHdr || variantFilesHdr.length === 0) return;

    const loadedMaps = {};
    let loadedCount = 0;

    variantFilesHdr.forEach((file, index) => {
      const rgbeLoader = new RGBELoader();
      rgbeLoader.load(
        file,
        (texture) => {
          // Configure the texture properly for lightmapping
          texture.flipY = false;
          texture.wrapS = THREE.ClampToEdgeWrapping;
          texture.colorSpace = THREE.NoColorSpace; // For non-color data like lightmaps
          texture.minFilter = THREE.LinearFilter;
          texture.magFilter = THREE.LinearFilter;
          texture.generateMipmaps = false; // Often better for lightmaps
          texture.channel = 1;

          //Match name with mesh name
          const targetMeshBase = findHdrMatchingMeshName(file, gltfScene);

          console.log('texture, targetMeshBaseName, intensity, loadedCount', texture, targetMeshBase, params.lightMapIntensity, loadedCount);

          // Store the loaded texture with its configuration
          loadedMaps[index] = {
            texture: texture,
            targetMeshBaseName: targetMeshBase,
            intensity: params.lightMapIntensity
          };

          loadedCount++;

          // When all lightmaps are loaded, update the state
          if (loadedCount === variantFilesHdr.length) {
            console.log('Loaded maps:', loadedMaps);
            setLightMaps(loadedMaps);
          }
        },
        undefined,
        (error) => {
          console.error(`Error loading lightmap ${index}:`, error);
          loadedCount++;

          // Continue even if some lightmaps fail to load
          if (loadedCount === variantFilesHdr.length) {
            setLightMaps(loadedMaps);
          }
        }
      );
    });
  }, [variantFilesHdr, params.lightMapIntensity]);

  useEffect(() => {
    if (!gltfScene) return;
    setGltfScene(gltfScene);
    if (variantFilesHdr.length == 0) {
      setLoadingLightMap(false);
      return;
    }
    if (Object.keys(lightMaps).length === 0) return;

    console.log('Applying lightmaps to meshes...');

    // Create a Map for quick lookup of lightmap textures
    const lightMapLookup = new Map();
    Object.values(lightMaps).forEach(({ targetMeshBaseName, texture }) => {
      if (targetMeshBaseName) {
        lightMapLookup.set(targetMeshBaseName.toLowerCase(), texture);
      }
    });

    console.log('LightMap Lookup:', lightMapLookup);

    // IMPORTANT: Check if materials are being shared between meshes
    console.log('=== Material Sharing Analysis ===');
    const materialMap = new Map();

    gltfScene.traverse((child) => {
      if (!child.isMesh) return;
      //if (child.material && child.material.color) child.material.color.set(0, 0, 1);
      // Store material references to check for sharing
      if (!materialMap.has(child.material.uuid)) {
        materialMap.set(child.material.uuid, []);
      }
      materialMap.get(child.material.uuid).push(child.name);

      console.log(`Mesh: "${child.name}", Material UUID: ${child.material.uuid}`);
    }
    );

    // Log which materials are shared
    console.log('=== Shared Materials ===');
    materialMap.forEach((meshNames, materialUUID) => {
      if (meshNames.length > 1) {
        console.log(`Material ${materialUUID} is shared by:`, meshNames);
      }
    });

    console.log('=== Cloning Materials ===');
    // Traverse the gltfScene **once** and apply the correct lightmap
    // Clone materials to ensure each mesh has its own material instance
    gltfScene.traverse((child) => {
      if (!child.isMesh) return;

      const childNameLower = child.name.toLowerCase();

      if (child.isMesh && childNameLower.includes("floor")) {
        if (child.material) {
          // Clone the material to avoid affecting other objects
          child.material = child.material.clone();

          // Fix for alpha map visibility
          child.material.transparent = true;
          // child.material.alphaTest = 0.001; // Adjust this value (0.01-0.5) to change shadow intensity

          // This is critical - change how alpha blending works
          // child.material.blending = THREE.MultiplyBlending; // Try different blending modes
          // Other modes to try: THREE.NormalBlending, THREE.AdditiveBlending

          child.material.needsUpdate = true;
          console.log("Modified floor material:", child.material);
        }
      }

      // Find a matching lightmap based on the child's name
      for (const [baseName, texture] of lightMapLookup) {
        if (childNameLower.includes(baseName)) {
          // Clone material only if we need to modify it
          console.log(`Applying "${baseName}" lightmap to: "${child.name}"`);
          // Clone the material to ensure it's not shared
          child.material = child.material.clone();
          console.log(`Cloned material for: "${child.name}", new UUID: ${child.material.uuid}`);
          child.material.lightMap = texture;
          child.material.lightMapIntensity = params.lightMapIntensity;
          child.material.needsUpdate = true;

          console.log(`Applied lightmap to: "${child.name}"`);
          break; // No need to check further once matched
        }
      }
    }
    );

    setLoadingLightMap(false);

    //If these don't change, we won't set loading light map to false. 
  }, [gltfScene, lightMaps, variantFileGlb, params.lightMapIntensity]);

  // // Store reference to the mixer for animation control
  useEffect(() => {
    if (mixer) {
      mixerRef.current = mixer;
    }
  }, [mixer]);

  // Start or stop animations when the button is clicked
  //TODO: Move the animation button to the buttom right
  useEffect(() => {
    //if (!isFirstAnimToggled && !isSecondAnimToggled) return;
    if (actions && animations && animations.length > 0) {
      const action = actions[Object.keys(actions)[0]];
      const mapping = productSettings.animationMapping || {};
      const animName = mapping[selectedMeshName];
      //animPlaying is false for named mesh
      console.log('animationPlaying changed:', animationPlaying, animName, animationDirection);
      console.log('lastAnimationName:', lastAnimationName);
      if (animationDirection === 1 && ((lastAnimationName && animName && lastAnimationName !== animName) ||
        (!lastAnimationName && animName) || (lastAnimationName && !selectedMeshName))) {
        //Stop the last animation if it's different from the current one
        const animationToStop = lastAnimationName || Object.keys(actions)[0];
        //The first meshName run will always trigger this => check if animation is at the end
        console.log('Stopping previous animation:', animationToStop);

        if (actions[animationToStop] && actions[animationToStop].time == actions[animationToStop].getClip().duration) {
          actions[animationToStop].stop();
          actions[animationToStop].reset();
          actions[animationToStop].setLoop(THREE.LoopOnce, 1);
          actions[animationToStop].time = actions[animationToStop].getClip().duration;
          actions[animationToStop].timeScale = -1;
          actions[animationToStop].clampWhenFinished = true;
          actions[animationToStop].play();

          // Once the animation has reset to the start, we can play the new one
          const onFinishedReset = () => {
            console.log('Previous animation reset finished, playing new animation');
            actions[animationToStop].stop();
            actions[animationToStop].reset();
            playNewAnimation(selectedMeshName, animName, action, mapping);
            actions[animationToStop]._mixer.removeEventListener('finished', onFinishedReset);
          };
          actions[animationToStop]._mixer.addEventListener('finished', onFinishedReset);
        }
        else {
          actions[animationToStop].stop();
          actions[animationToStop].reset();
          playNewAnimation(selectedMeshName, animName, action, mapping);
        }
      } else {
        playNewAnimation(selectedMeshName, animName, action, mapping);
      }
      if (animName) { setLastAnimationName(animName); }
      else { setLastAnimationName(null); }
    }
  }, [animationPlaying, actions, animations]);

  useEffect(() => {
    // Create coordinates for dimension vectors and labels
    //Need to capture this when the model is straight only
    if (viewerModeRef.current === "360" || productSettings.simpleDimensions) return;
    console.log('fetching dimensions');
    const fetchDimensions = async () => {
      // const exampleSofaDimensions = await fetch("/components/ExampleSofaDimensions.json")
      //   .then((response) => response.json());
      // console.log("ExampleSofaDimensions:", exampleSofaDimensions);

      let tempObjectDimensions = objectDimensions;
      let isCalculated = false;
      if ((!tempObjectDimensions && box) || (!productSettings.simpleDimensions && !productSettings.advancedDimensions && box)) {
        tempObjectDimensions = GetObjectExtDimensions(box);
        console.log('temp object dimensions:', tempObjectDimensions);
        isCalculated = true;
      }

      //Pass units from objectDimensions in priority
      const coordinateData = CreateCoordinates(size, isCalculated, {
        ObjectDimensions: tempObjectDimensions,
      }, productSettings.numDigits,
        (tempObjectDimensions?.modelSetup?.units) ?
          tempObjectDimensions.modelSetup.units : productSettings.units);
      console.log('coordinate data:', coordinateData, box);
      setCoordinateData(coordinateData);
    };

    fetchDimensions();
  }, [size, box, productSettings.simpleDimensions]);

  const { activeSetDataRef, activeSetNameRef } = useShowCorrectCoordinates(
    showLabels,
    center,
    { coordinateData },
    controls
  );

  // Store refs for dimension components to update directly
  const dimensionLine0Ref = useRef(null);
  const dimensionLine1Ref = useRef(null);
  const dimensionLine2Ref = useRef(null);
  const dimensionLabel0Ref = useRef(null);
  const dimensionLabel1Ref = useRef(null);
  const dimensionLabel2Ref = useRef(null);
  const arrowhead0StartRef = useRef(null);
  const arrowhead0EndRef = useRef(null);
  const arrowhead1StartRef = useRef(null);
  const arrowhead1EndRef = useRef(null);
  const arrowhead2StartRef = useRef(null);
  const arrowhead2EndRef = useRef(null);

  const prevActiveSetRef = useRef(null);

  // Update dimension components directly via refs in useFrame - bypasses React reconciliation
  useFrame(() => {
    if (!showLabels || !isCameraSettled || productSettings.simpleDimensions) return;
    
    // Read active set directly from ref (no state = no React reconciliation)
    const activeSetName = activeSetNameRef.current;
    const activeSetData = activeSetDataRef.current;
    
    // Only update if activeSet actually changed
    if (!activeSetName || !activeSetData || activeSetName === prevActiveSetRef.current) return;
    
    prevActiveSetRef.current = activeSetName;
    
    // Update dimension line positions directly via refs (no React reconciliation)
    if (activeSetData[0] && dimensionLine0Ref.current) {
      dimensionLine0Ref.current.updatePosition(activeSetData[0].start, activeSetData[0].end);
    }
    if (activeSetData[1] && dimensionLine1Ref.current) {
      dimensionLine1Ref.current.updatePosition(activeSetData[1].start, activeSetData[1].end);
    }
    if (activeSetData[2] && dimensionLine2Ref.current) {
      dimensionLine2Ref.current.updatePosition(activeSetData[2].start, activeSetData[2].end);
    }
    
    // Update label positions and text directly via refs (no React reconciliation)
    if (activeSetData[0] && dimensionLabel0Ref.current) {
      if (activeSetData[0].label) {
        dimensionLabel0Ref.current.updatePosition(activeSetData[0].label);
      }
      if (activeSetData[0].labelText) {
        dimensionLabel0Ref.current.updateText(activeSetData[0].labelText);
      }
    }
    if (activeSetData[1] && dimensionLabel1Ref.current) {
      if (activeSetData[1].label) {
        dimensionLabel1Ref.current.updatePosition(activeSetData[1].label);
      }
      if (activeSetData[1].labelText) {
        dimensionLabel1Ref.current.updateText(activeSetData[1].labelText);
      }
    }
    if (activeSetData[2] && dimensionLabel2Ref.current) {
      if (activeSetData[2].label) {
        dimensionLabel2Ref.current.updatePosition(activeSetData[2].label);
      }
      if (activeSetData[2].labelText) {
        dimensionLabel2Ref.current.updateText(activeSetData[2].labelText);
      }
    }
    
    // Helper function to update arrowhead position using Arrowhead component's logic
    const updateArrowhead = (mesh, position, direction, scaleFactor, distanceScale, lineOffset, minArrowHeight) => {
      if (!mesh) return;
      
      const arrowHeight = 0.5; // Default arrowHeight from Arrowhead component
      const lineLength = new THREE.Vector3().subVectors(position, direction).length();
      const directionVector = new THREE.Vector3().subVectors(direction, position).normalize();
      
      // Calculate scaledHeight (same logic as Arrowhead component)
      const scaledHeight = THREE.MathUtils.clamp(
        (arrowHeight * lineOffset * distanceScale / scaleFactor),
        minArrowHeight / scaleFactor,
        lineLength * 0.4
      );
      
      // Calculate lineOffSetTotal (same logic as Arrowhead component)
      const lineOffSetTotal = THREE.MathUtils.clamp(
        (lineOffset / scaleFactor) + scaledHeight,
        minArrowHeight / scaleFactor,
        lineLength * 0.25
      );
      
      let arrowDirection;
      let arrowPosition;
      
      if (scaledHeight > (0.2 * lineLength)) {
        // Point inwards for short lines
        arrowDirection = directionVector.clone();
        arrowPosition = new THREE.Vector3().copy(position)
          .sub(directionVector.clone().multiplyScalar(lineOffSetTotal))
          .add(directionVector.clone().multiplyScalar(scaledHeight / 2));
      } else {
        // Point outwards for normal lines
        arrowDirection = directionVector.clone().negate();
        arrowPosition = new THREE.Vector3().copy(position)
          .add(directionVector.clone().multiplyScalar(lineOffSetTotal))
          .sub(directionVector.clone().multiplyScalar(scaledHeight / 2));
      }
      
      mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), arrowDirection);
      mesh.position.copy(arrowPosition);
    };
    
    // Update arrowhead positions directly via Three.js mesh refs
    if (activeSetData[0]) {
      const line0 = activeSetData[0];
      const scaleFactor0 = line0.scaleFactor || 1;
      updateArrowhead(
        arrowhead0StartRef.current,
        line0.start,
        line0.end,
        scaleFactor0,
        distanceScale,
        params.lineOffset,
        minArrowHeight
      );
      updateArrowhead(
        arrowhead0EndRef.current,
        line0.end,
        line0.start,
        scaleFactor0,
        distanceScale,
        params.lineOffset,
        minArrowHeight
      );
    }
    if (activeSetData[1]) {
      const line1 = activeSetData[1];
      const scaleFactor1 = line1.scaleFactor || 1;
      updateArrowhead(
        arrowhead1StartRef.current,
        line1.start,
        line1.end,
        scaleFactor1,
        distanceScale,
        params.lineOffset,
        minArrowHeight
      );
      updateArrowhead(
        arrowhead1EndRef.current,
        line1.end,
        line1.start,
        scaleFactor1,
        distanceScale,
        params.lineOffset,
        minArrowHeight
      );
    }
    if (activeSetData[2]) {
      const line2 = activeSetData[2];
      const scaleFactor2 = line2.scaleFactor || 1;
      updateArrowhead(
        arrowhead2StartRef.current,
        line2.start,
        line2.end,
        scaleFactor2,
        distanceScale,
        params.lineOffset,
        minArrowHeight
      );
      updateArrowhead(
        arrowhead2EndRef.current,
        line2.end,
        line2.start,
        scaleFactor2,
        distanceScale,
        params.lineOffset,
        minArrowHeight
      );
    }
  });

  //TODO: Only run this when the animated.group rotation is settled
  /*<animated.group position={[0, pivotPoint.y, 0]} rotation-z={rotationZ} rotation-y={rotationY}> */
  useEffect(() => {
    console.log('viewer mode, rotation settled:', viewerModeRef.current, isRotationSettled, dimensionsCalculated);
    if (gltfScene && (!viewerModeRef.current || viewerModeRef.current !== "360") && isRotationSettled && isCamRotationSettled && !dimensionsCalculated) {

      console.log('GLTF Scene Loaded:', gltfScene);  // Debugging model loading
      // Traverse through the GLTF scene and set all meshes to cast and receive shadows
      gltfScene.traverse((child) => {
        if (child.isMesh) {
          child.castShadow = productSettings.isBaked ? false : true;  // Enable casting shadows
          child.receiveShadow = productSettings.isBaked ? false : true;  // Enable receiving shadows
        }
      });

      modelRef.current = gltfScene;

      console.log('MODEL REF:', modelRef.current);

      const meshBox = computeScaledBoundingBox(gltfScene);

      const center = new THREE.Vector3();
      const size = new THREE.Vector3();
      meshBox.getCenter(center);
      meshBox.getSize(size);
      setCenter(center);
      setSize(size);
      setBox(meshBox);
      setDimensionsCalculated(true);
    }
  }, [gltfScene, isRotationSettled, isCamRotationSettled, viewerModeRef.current]);

  if (gltfScene) {
    gltfScene.traverse((node) => {
      if (node.isMesh && node.material) {
        const material = node.material;
        // Check if custom properties exist in material's userData
        if (material.userData && material.userData.extras) {
          console.log("Custom Material Properties:", material.userData.extras);

          if (material.userData.extras.objectType === "jewellery") {
            params.modelScale = 10;
            params.environmentIntensity = 1;
            params.enablePan = true;
            params.fullAngle = true;
          } else {
            //Non exclusive if statements so we can accept several tags, glass and reflective override fabrics
            if (material.userData.extras.materialType === "fabric") {
              console.log("fabrics");
              params.environmentIntensity = 0;
            }
            if (material.userData.extras.surfaceType === "reflective") {
              console.log("reflective");
              params.environmentIntensity = 1;
            }
            if (material.userData.extras.surfaceType === "glass") {
              console.log("glass");
              params.environmentIntensity = 1;
              params.enablePan = true;
              params.fullAngle = true;
            }
            if (material.userData.extras.camType === "freeCam") {
              console.log("free cam");
              params.enablePan = true;
              params.fullAngle = true;
            }
          }
        }
      }
    });
  }

  // Calculate new x and z positions for the spotlight in a circular path
  const x = params.lightRadius * Math.cos(params.light_position); // X-coordinate
  const z = params.lightRadius * Math.sin(params.light_position); // Z-coordinate

  //Shift box if the box min vertical axis isn't at 0
  const computeCorrectedBoundingBox = (box, object) => {
    if (!object) return;

    // Correct only if the center is off
    if (box.min.y !== 0) {
      const offset = new THREE.Vector3(0, -box.min.y, 0);
      box.min.add(offset);
      box.max.add(offset);
    }
    return box;
  };

  const computeScaledBoundingBox = (object) => {
    const box = new THREE.Box3().setFromObject(object);
    const originalBox = new THREE.Box3().setFromObject(object);
    const originalCenter = new THREE.Vector3();
    originalBox.getCenter(originalCenter);


    //TODO: Check if this always works
    let isMeshContainsFloor = false;
    gltfScene.traverse((child) => {
      if (child.name.toLowerCase().includes("floor")) {
        isMeshContainsFloor = true;
      }
    });

    if (!isMeshContainsFloor) return new THREE.Box3().setFromObject(object);

    const traverseWithScale = (child, parentScale) => {
      // Compute total scale including inherited parent scale
      const totalScale = parentScale.clone().multiply(child.scale);

      if (child.isMesh && !child.name.toLowerCase().includes("floor")) {

        // Compute the bounding box for this mesh
        const childBox = new THREE.Box3().setFromObject(child);

        // Apply the total scale
        childBox.min.multiply(totalScale);
        childBox.max.multiply(totalScale);

        // Expand the overall bounding box
        box.expandByPoint(childBox.min);
        box.expandByPoint(childBox.max);
      }

      // Continue traversal for all children
      child.children.forEach((grandChild) => traverseWithScale(grandChild, totalScale));
    };

    // Start traversal with default scale (1,1,1)
    traverseWithScale(object, new THREE.Vector3(1, 1, 1));

    console.log('FINAL BOUNDING BOX:', box);
    return computeCorrectedBoundingBox(box, object);
  };


  useEffect(() => {
    if (!gltfScene || !camera || !controls?.current) return;

    // Use ref to avoid re-renders
    const currentViewerMode = viewerModeRef.current;
    const box = computeScaledBoundingBox(gltfScene);
    const scaleUpFactor = isSmallObject ? params.smallObjectScaleUp : 1;
    const scaledSize = new THREE.Vector3();
    box.getSize(scaledSize).multiplyScalar(scaleUpFactor); // Scale the size
    const center = new THREE.Vector3();
    const size = new THREE.Vector3();
    box.getCenter(center);
    box.getSize(size);

    // Compute diagonal length of the bounding box
    const boundingDiagonal = size.length();
    const fov = camera.fov * (Math.PI / 180); // Convert FOV to radians
    console.log('camera fov:', fov);
    const aspectRatio = camera.aspect;
    const fitHeightDistance = boundingDiagonal / (2 * Math.tan(fov / 2));
    const fitWidthDistance = fitHeightDistance / aspectRatio;
    //const idealDistance = Math.max(fitHeightDistance, fitWidthDistance) * 1.2; // Add padding
    const targetPosition = center.clone();
    // const newCameraPosition = new THREE.Vector3(
    //   center.x,
    //   center.y + 0.1, // Slightly above the object
    //   center.z + (viewerMode === '360' ? idealDistance * 1.1 : idealDistance * 1.3) // Adjust distance based on mode
    // );

    // Helper function to calculate shortest angle difference
    const shortestAngle = (a, b) => {
      const diff = (b - a + 180) % 360 - 180;
      return diff < -180 ? diff + 360 : diff;
    };

    // Calculate camera position and target
    const idealDistance = Math.max(
      boundingDiagonal / (2 * Math.tan(fov / 2)),
      (boundingDiagonal / (2 * Math.tan(fov / 2))) / aspectRatio
    ) * 1.2;

    // Calculate new camera position based on yaw and pitch
    // Create a vector from the object's center to the camera position
    const cameraOffset = new THREE.Vector3();
    cameraOffset.setFromSpherical(new THREE.Spherical(
      idealDistance,
      THREE.MathUtils.degToRad(pitchAngle || 90),
      THREE.MathUtils.degToRad(yawAngle || 0)
    ));

    // The final camera position is the object center plus the offset
    const newCameraPosition = center.clone().add(cameraOffset);

    // Ensure the camera always points at the object's center
    camera.lookAt(center);

    // Animate both position and rotation together
    if (controls && controls.current) {
      const controlsRef = controls.current;

      // First, set the immediate target position
      //controlsRef.target.copy(targetPosition);

      // Calculate the spherical coordinates for the animation
      const spherical = new THREE.Spherical();
      //center.z + targetZoom
      //Need to set camera.position to be close to the object to avoid large jumps
      if (camera.position.distanceTo(center) > idealDistance * 2) {
        camera.position.set(newCameraPosition.x, newCameraPosition.y, newCameraPosition.z + idealDistance * 2);
      }
      spherical.setFromVector3(camera.position.clone().sub(center));

      // Calculate current angles in degrees
      const currentYaw = THREE.MathUtils.radToDeg(spherical.theta);
      const currentPitch = THREE.MathUtils.radToDeg(spherical.phi);

      // Calculate shortest angles to target
      const targetYaw = yawAngle || 0;
      const targetPitch = pitchAngle || 90;
      const shortestYaw = startingYawSet ? 0 : shortestAngle(currentYaw, targetYaw);
      const shortestPitch = shortestAngle(currentPitch, targetPitch);

      setStartingYawSet(true);
      // Animate the spherical coordinates using shortest angles
      //We're always resetting to a certain yaw and pitch. Disable yaw
      gsap.to(spherical, {
        duration: 1,
        theta: THREE.MathUtils.degToRad(currentYaw + shortestYaw),
        phi: THREE.MathUtils.degToRad(currentPitch + shortestPitch),
        ease: "power2.out",
        onStart: () => {
          setIsCamRotationSettled(false);
          setIsObjectPanning(true);
        },
        onUpdate: () => {
          // Convert spherical coordinates back to cartesian
          const newCameraPosition = new THREE.Vector3();
          newCameraPosition.setFromSpherical(spherical);

          // Add the object's center
          newCameraPosition.add(center);

          // Update camera position and look at target
          camera.position.copy(newCameraPosition);
          camera.lookAt(center);

          // Update controls
          controlsRef.update();
        },
        onComplete: () => {
          controlsRef.update();
          console.log('Camera animation complete');
          // Only trigger bounds refresh if not already in progress (to avoid duplicate triggers during viewerMode transitions)
          // The viewerMode change handler will have already triggered bounds if needed

          setIsCamRotationSettled(true);
          setIsObjectPanning(false);
        }
      });
    }

    console.log(`Camera updated: Pitch ${pitchAngle}°, Yaw ${yawAngle}°`);
  }, [yawAngle, pitchAngle, forceUpdate])


  useEffect(() => {
    // Update maxPolarAngle for labels, jewellery or freecam objects
    const fullAngle = params.fullAngle;
    if (!controls.current || !gltfScene || !camera) return;

    const currentViewerMode = viewerModeRef.current;
    controls.current.maxPolarAngle = ((showLabels || fullAngle) && currentViewerMode !== '360') ? Math.PI : Math.PI / 2;
    controls.current.minPolarAngle = currentViewerMode === '360' ? Math.PI / 2 : 0;
    console.log('max polar angle:', controls.current.maxPolarAngle);

    const updateZoomLimits = () => {
      // Add check for existing zoom limits
      if (controls.current.minDistance !== 0 && controls.current.maxDistance !== 0) {
        return; // Skip if zoom limits are already set
      }

      const box = computeScaledBoundingBox(gltfScene);
      console.log('NEW BOX:', box);
      const scaleUpFactor = isSmallObject ? params.smallObjectScaleUp : 1;
      const scaledSize = new THREE.Vector3();
      box.getSize(scaledSize).multiplyScalar(scaleUpFactor); // Scale the size
      const center = new THREE.Vector3();
      const size = new THREE.Vector3();
      box.getCenter(center);
      box.getSize(size);

      // Compute diagonal length of the bounding box
      const boundingDiagonal = size.length();

      // Determine ideal distance using field of view
      //camera.fov = 30;

      const fov = camera.fov * (Math.PI / 180); // Convert FOV to radians
      const aspectRatio = camera.aspect;
      const fitHeightDistance = boundingDiagonal / (2 * Math.tan(fov / 2));
      console.log('fitHeightDistance:', fitHeightDistance);
      const fitWidthDistance = fitHeightDistance / aspectRatio;
      const idealDistance = Math.max(fitHeightDistance, fitWidthDistance) * 0.6; // Add padding

      console.log('fov:', fov);

      //console.log('Ideal Camera Distance:', idealDistance);

      // Set camera position based on ideal distance
      const direction = new THREE.Vector3(0, 0, 1); // Default forward direction
      direction.applyQuaternion(camera.quaternion); // Align with camera orientation
      const newPosition = center.clone().addScaledVector(direction, idealDistance);

      camera.position.copy(newPosition);
      camera.lookAt(center);
      camera.updateProjectionMatrix();

      console.log('Ideal Camera Distance:', idealDistance);
      console.log('zoom close:', params.zoomClose);
      console.log('min zoom close:', params.minZoomClose);
      console.log('max zoom factor:', params.maxZoomFactor, productSettings.maxZoomFactor);

      // Set zoom constraints
      const minZoom = idealDistance * (productSettings.zoomClose || params.zoomClose);
      const maxZoom = idealDistance * (productSettings.maxZoomFactor || params.maxZoomFactor);

      console.log("min zoom:", minZoom, "max zoom:", maxZoom);

      // Apply dynamic zoom limits
      controls.current.minDistance = minZoom;
      controls.current.distance = idealDistance;
      controls.current.maxDistance = maxZoom;
      // Set the control target to the center of the model
      controls.current.target.copy(center);
      controls.current.update();
      
      // Delay rendering a bit to let OrbitControls settle (optional)
      setTimeout(() => setReadyToRender(true), 250); 
    }

    // Initial zoom limits setup
    updateZoomLimits();

    const toggleFloorMeshesVisibility = (object, showLabels) => {
      if (object.isMesh && object.name.toLowerCase().includes("floor")) {
        object.visible = !showLabels; // Toggle visibility
        console.log(`Floor mesh '${object.name}' visibility set to: ${object.visible}`);
      }

      // Recursively check children
      object.children.forEach(child => toggleFloorMeshesVisibility(child, showLabels));
    };

    // Call the function on the root scene
    toggleFloorMeshesVisibility(gltfScene, showLabels);
    console.log('set iscamerasettled true');
    setIsCameraSettled(true);

  }, [showLabels, gltfScene, variantFileGlb]);

  const cameraTimelineRef = useRef();

  useEffect(() => {
    if (isObjectPanning && gltfScene && enableInteractionHint && controls.current && showInteractionHint) {
      // Get current camera position relative to target
      const currentTarget = controls.current.target.clone();
      const currentPosition = controls.current.object.position.clone();
      const radius = currentPosition.distanceTo(currentTarget);

      // Calculate current spherical coordinates
      const spherical = new THREE.Spherical();
      spherical.setFromVector3(currentPosition.clone().sub(currentTarget));

      const originalTheta = spherical.theta;

      // Store timeline reference
      cameraTimelineRef.current = gsap.timeline()
        .to(spherical, {
          theta: originalTheta + 0.3,
          duration: 1,
          ease: "power2.inOut",
          onUpdate: () => {
            const newPosition = new THREE.Vector3().setFromSpherical(spherical).add(currentTarget);
            controls?.current?.object?.position.copy(newPosition);
            controls?.current?.update();
          }
        })
        .to(spherical, {
          theta: originalTheta - 0.3,
          duration: 1,
          ease: "power2.inOut",
          onUpdate: () => {
            const newPosition = new THREE.Vector3().setFromSpherical(spherical).add(currentTarget);
            controls?.current?.object?.position.copy(newPosition);
            controls?.current?.update();
          }
        })
        .to(spherical, {
          theta: originalTheta,
          duration: 1,
          ease: "power2.inOut",
          onUpdate: () => {
            const newPosition = new THREE.Vector3().setFromSpherical(spherical).add(currentTarget);
            controls?.current?.object?.position.copy(newPosition);
            controls?.current?.update();
          }
        });
    } else if (!enableInteractionHint && cameraTimelineRef.current) {
      cameraTimelineRef.current.kill();
    }
  }, [isObjectPanning, gltfScene, enableInteractionHint]);

  // Calculate bounding box for pivot point
  const pivotPoint = useMemo(() => {
    if (!gltfScene) return new THREE.Vector3(0, 0, 0);

    const box = new THREE.Box3().setFromObject(gltfScene);
    const center = box.getCenter(new THREE.Vector3());

    return new THREE.Vector3(0, center.y, 0); // Use center.y directly for middle of screw
  }, [gltfScene, isRotationSettled]);

  // Initialize rotation
  //Reset model when switching to 3D to keep model origin centered
  // Use imperative API to update spring without causing full component re-renders
  const [springStyles, springApi] = useSpring(() => ({
    rotationZ: viewerModeRef.current === "360" ? THREE.MathUtils.degToRad(productSettings.tiltAngle || 0) : 0,
    rotationY: viewerModeRef.current === "360" ? THREE.MathUtils.degToRad(productSettings.panAngle || 0) : 0,
    config: { mass: 1, tension: 280, friction: 60 },
    onStart: () => {
      //This is causing the camera to jump at the end of the fitting
      //It's still jerky, but a bit better. Probably because we're setting state variables that trigger re-renders.
      //setIsRotationSettled(false);
      //setIsObjectPanning(true);
    },
    onRest: () => {
      // Smoothly animate OrbitControls target when switching modes
      
      if (controls.current && gltfScene) {
        const box = new THREE.Box3().setFromObject(gltfScene);
        const newCenter = box.getCenter(new THREE.Vector3());
        const currentTarget = controls.current.target.clone();
        gsap.to(currentTarget, {
          x: newCenter.x,
          y: newCenter.y,
          z: newCenter.z,
          duration: 0.6,
          ease: "power2.out",
          onUpdate: () => {
            controls.current.target.copy(currentTarget);
            controls.current.update();
          },
          onComplete: () => {
            //setIsRotationSettled(true);
            //setIsObjectPanning(false);
          }
        });
      }
    }
  }));
  const { rotationZ, rotationY } = springStyles;
  
  // Update spring animation when viewerModeRef changes (using imperative API to avoid re-renders)
  // Poll for changes to avoid re-renders
  useEffect(() => {
    let lastViewerMode = viewerModeRef.current;
    
    const updateSpring = () => {
      const currentViewerMode = viewerModeRef.current;
      if (currentViewerMode !== lastViewerMode) {
        lastViewerMode = currentViewerMode;
        const is360 = currentViewerMode === "360";
        springApi.start({
          rotationZ: is360 ? THREE.MathUtils.degToRad(productSettings.tiltAngle || 0) : 0,
          rotationY: is360 ? THREE.MathUtils.degToRad(productSettings.panAngle || 0) : 0,
        });
      }
    };
    
    // Check immediately
    updateSpring();
    
    // Poll for changes
    const interval = setInterval(updateSpring, 100);
    return () => clearInterval(interval);
  }, [productSettings.tiltAngle, productSettings.panAngle, springApi]);

  //Prevent panning with 2 fingers
  useEffect(() => {
    const ctrl = controls.current;
    if (!ctrl) return;

    ctrl.enablePan = false;
    ctrl.enableZoom = true;
    ctrl.touches = {
      ONE: THREE.TOUCH.ROTATE,
      TWO: THREE.TOUCH.DOLLY_ROTATE,
    };

  }, [canvasRef.current, controls]);

  // Floor mesh visibility toggle
  useEffect(() => {
    if (!gltfScene) return;

    const toggleFloorMeshesVisibility = (object, showLabels) => {
      if (object.isMesh && object.name.toLowerCase().includes("floor")) {
        object.visible = !showLabels;
        console.log(`Floor mesh '${object.name}' visibility set to: ${object.visible}`);
      }
      object.children.forEach(child => toggleFloorMeshesVisibility(child, showLabels));
    };

    toggleFloorMeshesVisibility(gltfScene, showLabels);
    setIsCameraSettled(true);
  }, [showLabels, gltfScene, variantFileGlb]);

  useEffect(() => {
    if (controls.current) {
      const handleControlStart = () => {
        // Hide interaction hint when user starts interacting
        if (setEnableInteractionHint) {
          setEnableInteractionHint(false);
        }
      };

      controls.current.addEventListener('start', handleControlStart);

      return () => {
        if (controls.current) {
          controls.current.removeEventListener('start', handleControlStart);
        }
      };
    }
  }, [controls.current, setEnableInteractionHint]);

  function BoundsRefresher({
    modelRef,
    modelAnimationFinished = false,
    setModelAnimationFinished,
    setOrbitControlsEnabled,
    boundsMaxDuration = 0.5,
  }) {

    const bounds = useBounds();
    const timeoutRef = useRef(null);
    const triggeredRef = useRef(false);
    const isCleaningUpRef = useRef(false);
    const boundsRefreshCompletedRef = useRef(false);

    useEffect(() => {
      const group = modelRef?.current?.getGroup?.() ?? modelRef?.current ?? undefined;

      // If already triggered and timeout pending, don't re-trigger
      if (!group || !modelAnimationFinished || triggeredRef.current) return;

      // Reset cleanup flag and completion flag when starting new animation
      isCleaningUpRef.current = false;
      boundsRefreshCompletedRef.current = false;

      // Perform a single bounds refresh (safe-guarded)
      try {
        bounds.refresh(group).clip().fit();
      } catch (err) {
        console.warn("Bounds refresh failed:", err);

        boundsRefreshCompletedRef.current = true;
        return;
      }

      // Mark as triggered so repeated renders don't retrigger while waiting
      triggeredRef.current = true;

      // Schedule clearing of flags after bounds animation completes
      const waitMs = Math.max(50, Math.round(boundsMaxDuration * 1000) + 50);
      console.log(`BoundsRefresher: Setting timeout to clear boundsAnimating in ${waitMs}ms`);
      if (timeoutRef.current) {
        console.log('BoundsRefresher: Clearing existing timeout');
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        // Only execute if we're not in cleanup mode
        if (isCleaningUpRef.current) {
          console.log('BoundsRefresher: Timeout fired but cleanup is in progress, skipping');
          return;
        }
        boundsRefreshCompletedRef.current = true;
        if (typeof setModelAnimationFinished === "function") {
          try { setModelAnimationFinished(false); } catch (e) { }
        }
        if (typeof setOrbitControlsEnabled === "function") {
          try { setOrbitControlsEnabled(true); } catch (e) { }
        }
        
        // reset trigger so future animations can retrigger
        triggeredRef.current = false;
        timeoutRef.current = null;
      }, waitMs);
    

      // cleanup on dependency change
      return () => {
        console.log('BoundsRefresher: Cleanup called - marking as cleaning up');
        isCleaningUpRef.current = true;
        if (timeoutRef.current) {
          console.log('BoundsRefresher: Clearing timeout in cleanup');
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
 
        } 
        triggeredRef.current = false;
        if (typeof setOrbitControlsEnabled === "function") {
          try { setOrbitControlsEnabled(true); } catch (e) { }
        }
      };
      // Only watch these flags and refs — this effect runs when the triggers change
    }, [modelAnimationFinished]);

    // ensure we cleanup on unmount
    useEffect(() => {
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        triggeredRef.current = false;
      };
    }, []);

    return null;

  }

  const Model = forwardRef(function Model({
    gltfScene,
    mixer,
    actions,
    pivotY = 0,
    isSmallObject = false,
    params = {},
    isCameraSettled = true,
    onAnimationStateChange
  }, ref) {
    // internal refs
    const groupRef = useRef(null);

    // keep stable refs for mixer/actions so useImperativeHandle callbacks don't get stale
    const mixerRef = useRef(mixer);
    useEffect(() => { mixerRef.current = mixer; }, [mixer]);

    const actionsRef = useRef(actions);
    useEffect(() => { actionsRef.current = actions; }, [actions]);

    // stable ref for callback to avoid stale closures in event listeners
    const onAnimChangeRef = useRef(onAnimationStateChange);
    useEffect(() => { onAnimChangeRef.current = onAnimationStateChange; }, [onAnimationStateChange]);

    // prev-running ref for frame-based detection of finished -> not running
    const prevRunningRef = useRef(false);

    // Expose imperative API to parent
    useImperativeHandle(ref, () => ({
      // play returns duration (or undefined)
      play: (name, forward = true) => {
        const localActions = actionsRef.current;
        if (!localActions || !localActions[name]) return;
        const action = localActions[name];
        const duration = action.getClip?.()?.duration ?? 0;

        // stop other actions to avoid blending issues
        Object.entries(localActions).forEach(([k, a]) => {
          if (!a) return;
          if (k !== name) a.stop();
        });

        action.enabled = true;
        action.paused = false;
        action.reset();
        action.clampWhenFinished = true;
        action.setLoop(THREE.LoopOnce, 0);
        action.setEffectiveWeight(1);
        action.setEffectiveTimeScale(forward ? 1 : -1);

        const epsilon = 1e-4;
        action.time = forward ? 0 : Math.max(0, duration - epsilon);
        action.play();

        console.log('Model.play() called - setting boundsAnimating to true via onAnimationStateChange');
        onAnimationStateChange?.(true);
        return duration;
      },

      // return underlying group for bounds.refresh()
      getGroup: () => groupRef.current,

      // true if any action is currently running
      isAnyActionRunning: () => {
        const local = actionsRef.current || {};
        return Object.values(local).some(a => !!a && a.isRunning?.());
      }
    }), []);

    useEffect(() => {
      const mx = mixerRef.current;
      if (!mx) return;

      const handleMixerFinished = (event) => {
        // If no actions are running now, notify parent
        const anyRunning = Object.values(actionsRef.current || {}).some(a => a && a.isRunning && a.isRunning());
        if (!anyRunning) {
          console.log('Model mixer finished - setting boundsAnimating to false via onAnimationStateChange');
          onAnimChangeRef.current?.(false);
        }
      };

      mx.addEventListener('finished', handleMixerFinished);
      return () => {
        mx.removeEventListener('finished', handleMixerFinished);
      };
    }, []); // attach once; uses refs for dynamic state



    // Safety: log if gltfScene missing
    useEffect(() => {
      if (!gltfScene) console.warn("Model: gltfScene is falsy");
    }, [gltfScene]);

    // Render primitive inside a group (so getGroup returns a Group that wraps the primitive)
    return (
      <group ref={groupRef} position={[0, -pivotY, 0]}>
        {gltfScene ? (
          <primitive
            object={gltfScene}
            scale={isSmallObject ? (params.smallObjectScaleUp ?? 1) : 1}
            visible={isCameraSettled}
          />
        ) : null}
      </group>
    );
  });



  console.log("parent width threescene:", parentWidth);
  console.log("isAndroid:", params.isAndroid);
  console.log('is baked:', productSettings.isBaked);
  console.log('variant environments hdr', variantEnvironmentsHdr);
  console.log('variant environments hdr array', variantEnvironmentsHdr[0]);
  console.log('controlsEnabled:', controlsEnabled);

  console.log('rotation settled, object panning:', isRotationSettled, isObjectPanning);

  console.log('controls enabled:', orbitControlsEnabled, (isTouchDevice ? controlsEnabled : true));

  return (
    <>
      <color attach="background" args={[sceneBackgroundColour]} />
      <MemoryLoggerAndCleanup />
      <Bounds clip margin={1.1} maxDuration={boundsMaxDuration}>
        {/* margin={1.4} maxDuration={0.01}> */}
        <animated.group position={[0, pivotPoint.y, 0]} rotation-z={rotationZ} rotation-y={rotationY}>
          {readyToRender && (
            <Suspense fallback={null}>
              <Model
                ref={modelRef}
                gltfScene={gltfScene}
                mixer={mixer}
                actions={actions}
                pivotY={pivotPoint.y}
                isSmallObject={isSmallObject}
                params={params}
                isCameraSettled={isCameraSettled}
              />
              <BoundsRefresher
                modelRef={modelRef}
                modelAnimationFinished={animationFinished}
                setModelAnimationFinished={setAnimationFinished}
                setOrbitControlsEnabled={setOrbitControlsEnabled}
                boundsMaxDuration={boundsMaxDuration}
              />
            </Suspense>
        )}
        </animated.group>
      </Bounds>
      <OrbitControls
        makeDefault
        enableDamping={false}
        dampingFactor={0.1}
        enablePan={params.enablePan}
        ref={controls}
        enabled={orbitControlsEnabled && (isTouchDevice ? controlsEnabled : true)}
        //enabled={false}
      />
      {/* Ambient Light - general ambient light across the scene */}

      {/* Directional Light*/}
      {!productSettings.isBaked && (
        <directionalLight
          position={[0, 2, 1]}
          intensity={params.amblight_intensity}
          castShadow={
            params.isAndroid || productSettings.isBaked ? false : true
          }
          shadow-mapSize-width={params.shadowmapsize}
          shadow-mapSize-height={params.shadowmapsize}
          shadow-camera-near={params.shadowNear}
          shadow-camera-far={params.shadowFar}
          shadow-radius={params.shadowblurradius}
          shadow-blurSamples={params.shadowblursamples}
          shadow-bias={params.shadowbias}
        />
      )}
      {/* Ambient light */}
      {productSettings.isBaked &&
        <>
          <directionalLight intensity={params.dirlightIntensity} position={[0, 1, 1]}
            castShadow={true} receiveShadow={true} />
          {/* <ambientLight intensity={params.amblight_intensity} color={params.lights_colour} /> */}
        </>
      }
      {/* Spotlight Rear */}
      {!productSettings.isBaked && (
        <spotLight
          position={[0, params.lightheight, -2]}
          angle={params.lightAngle}
          penumbra={params.spotpenumbra}
          decay={params.spotdecay}
          intensity={params.spot_intensity}
          castShadow={
            params.isAndroid || productSettings.isBaked ? false : true
          }
          shadow-mapSize-width={params.shadowmapsize}
          shadow-mapSize-height={params.shadowmapsize}
          shadow-camera-near={params.shadowNear}
          shadow-camera-far={params.shadowFar}
          shadow-radius={params.shadowblurradius}
          shadow-blurSamples={params.shadowblursamples}
          shadow-bias={params.shadowbias}
        />
      )}
      {/* Spotlight Front */}
      {/*TODO: Update if necessary to remove spotlight when baked*/}
      {!productSettings.isBaked &&
        (
          <spotLight
            position={
              productSettings.isBaked
                ? [0, params.lightheight, 3]
                : [0, params.lightheight, 1]
            }
            angle={params.lightAngle}
            penumbra={params.spotpenumbra}
            decay={params.spotdecay}
            intensity={params.spot_intensity}
            castShadow={
              params.isAndroid || productSettings.isBaked ? false : true
            }
            shadow-mapSize-width={params.shadowmapsize}
            shadow-mapSize-height={params.shadowmapsize}
            shadow-camera-near={params.shadowNear}
            shadow-camera-far={params.shadowFar}
            shadow-radius={params.shadowblurradius}
            shadow-blurSamples={params.shadowblursamples}
            shadow-bias={params.shadowbias}
          />
        )}
      {/* Spotlight Right */}
      {!productSettings.isBaked && (
        <spotLight
          position={[-2, params.lightheight, 0]}
          angle={params.lightAngle}
          penumbra={params.spotpenumbra}
          decay={params.spotdecay}
          intensity={params.spot_intensity}
          castShadow={
            params.isAndroid || productSettings.isBaked ? false : true
          }
          shadow-mapSize-width={params.shadowmapsize}
          shadow-mapSize-height={params.shadowmapsize}
          shadow-camera-near={params.shadowNear}
          shadow-camera-far={params.shadowFar}
          shadow-radius={params.shadowblurradius}
          shadow-blurSamples={params.shadowblursamples}
          shadow-bias={params.shadowbias}
        />
      )}
      {/* Spotlight Left */}
      {!productSettings.isBaked && (
        <spotLight
          position={[2, params.lightheight, 0]}
          angle={params.lightAngle}
          penumbra={params.spotpenumbra}
          decay={params.spotdecay}
          intensity={params.spot_intensity}
          castShadow={productSettings.isBaked ? false : true}
          shadow-mapSize-width={params.shadowmapsize}
          shadow-mapSize-height={params.shadowmapsize}
          shadow-camera-near={params.shadowNear}
          shadow-camera-far={params.shadowFar}
          shadow-radius={params.shadowblurradius}
          shadow-blurSamples={params.shadowblursamples}
          shadow-bias={params.shadowbias}
        />
      )}
      {/* Spotlight Spot */}
      {showLabels && !params.isAndroid && !productSettings.isBaked && (
        <spotLight
          position={[x, params.lightheight, z]}
          angle={params.lightAngle}
          penumbra={params.spotpenumbra}
          decay={params.spotdecay}
          intensity={params.spot_intensity}
          castShadow={productSettings.isBaked ? false : true}
          shadow-mapSize-width={params.shadowmapsize}
          shadow-mapSize-height={params.shadowmapsize}
          shadow-camera-near={params.shadowNear}
          shadow-camera-far={params.shadowFar}
          shadow-radius={params.shadowblurradius}
          shadow-blurSamples={params.shadowblursamples}
          shadow-bias={params.shadowbias}
        />
      )}
      {/* Spotlight Under */}
      {(!params.isAndroid || isSmallObject) && !productSettings.isBaked && (
        <spotLight
          position={[0, -params.lightheight, 0]}
          angle={Math.PI / 2}
          penumbra={params.spotpenumbra}
          decay={params.spotdecay}
          intensity={params.spot_intensity}
          castShadow={
            params.isAndroid ? false : true
          }
          shadow-mapSize-width={params.shadowmapsize}
          shadow-mapSize-height={params.shadowmapsize}
          shadow-camera-near={params.shadowNear}
          shadow-camera-far={params.shadowFar}
          shadow-radius={params.shadowblurradius}
          shadow-blurSamples={params.shadowblursamples}
          shadow-bias={params.shadowbias}
        />
      )}
      {productSettings.simpleDimensions && objectDimensions && objectDimensions !== "" &&
        <SimpleCoordinates
          configJson={objectDimensions}
          objectDimensions={objectDimensions}
          box={box} gltfScene={gltfScene}
          params={params}
          isCameraSettled={isCameraSettled}
          showLabels={showLabels}
          line_width={line_width}
          isMobile={isMobile}
          sceneBackgroundColour={sceneBackgroundColour}
          scaledArrowHeight={scaledArrowHeight}
          distanceScale={distanceScale}
          minArrowHeight={minArrowHeight}
          setScaledArrowHeightOut={setScaledArrowHeight}
          dimensionsOnTop={dimensionsOnTop}
        />
      }

      {/* <AutoFitCamera cameraOffset={cameraOffset} modelRef={modelRef} /> */}

      {/* Render dimension components once - they update via refs directly, no React reconciliation */}
      {/* Components are always rendered when coordinateData exists - they update via refs in useFrame */}
      {isCameraSettled &&
        !productSettings.simpleDimensions &&
        showLabels &&
        coordinateData &&
        activeSetDataRef.current && activeSetDataRef.current[0] && (
          <>
            <DimensionLine
              ref={dimensionLine0Ref}
              params={params}
              line_width={line_width}
              start={activeSetDataRef.current[0].start}
              end={activeSetDataRef.current[0].end}
              visible={true}
              scaleFactor={activeSetDataRef.current[0].scaleFactor || 1}
              lineOffset={params.lineOffset}
              scaledArrowHeight={scaledArrowHeight}
              minArrowHeight={minArrowHeight}
            />
            <DimensionLabel
              ref={dimensionLabel0Ref}
              params={params}
              position={activeSetDataRef.current[0].label}
              label={activeSetDataRef.current[0].labelText}
              visible={true}
              isMobile={isMobile}
              inverse={sceneBackgroundColour === "black"}
            />
            <Arrowhead
              ref={arrowhead0StartRef}
              params={params}
              position={activeSetDataRef.current[0].start}
              direction={activeSetDataRef.current[0].end}
              visible={true}
              scaleFactor={activeSetDataRef.current[0].scaleFactor || 1}
              distanceScale={distanceScale}
              lineOffset={params.lineOffset}
              setScaledArrowHeightOut={handleScaledArrowHeightUpdate}
              minArrowHeight={minArrowHeight}
            />
            <Arrowhead
              ref={arrowhead0EndRef}
              params={params}
              position={activeSetDataRef.current[0].end}
              direction={activeSetDataRef.current[0].start}
              visible={true}
              scaleFactor={activeSetDataRef.current[0].scaleFactor || 1}
              distanceScale={distanceScale}
              lineOffset={params.lineOffset}
              setScaledArrowHeightOut={handleScaledArrowHeightUpdate}
              minArrowHeight={minArrowHeight}
            />
          </>
        )}
      {isCameraSettled &&
        !productSettings.simpleDimensions &&
        showLabels &&
        coordinateData &&
        activeSetDataRef.current && activeSetDataRef.current[1] && (
          <>
            <DimensionLine
              ref={dimensionLine1Ref}
              params={params}
              line_width={line_width}
              start={activeSetDataRef.current[1].start}
              end={activeSetDataRef.current[1].end}
              visible={true}
              scaleFactor={activeSetDataRef.current[1].scaleFactor || 1}
              lineOffset={params.lineOffset}
              scaledArrowHeight={scaledArrowHeight}
              minArrowHeight={minArrowHeight}
            />
            <DimensionLabel
              ref={dimensionLabel1Ref}
              params={params}
              position={activeSetDataRef.current[1].label}
              label={activeSetDataRef.current[1].labelText}
              visible={true}
              isMobile={isMobile}
              inverse={sceneBackgroundColour === "black"}
            />
            <Arrowhead
              ref={arrowhead1StartRef}
              params={params}
              position={activeSetDataRef.current[1].start}
              direction={activeSetDataRef.current[1].end}
              visible={true}
              scaleFactor={activeSetDataRef.current[1].scaleFactor || 1}
              distanceScale={distanceScale}
              lineOffset={params.lineOffset}
              setScaledArrowHeightOut={handleScaledArrowHeightUpdate}
              minArrowHeight={minArrowHeight}
            />
            <Arrowhead
              ref={arrowhead1EndRef}
              params={params}
              position={activeSetDataRef.current[1].end}
              direction={activeSetDataRef.current[1].start}
              visible={true}
              scaleFactor={activeSetDataRef.current[1].scaleFactor || 1}
              distanceScale={distanceScale}
              lineOffset={params.lineOffset}
              setScaledArrowHeightOut={handleScaledArrowHeightUpdate}
              minArrowHeight={minArrowHeight}
            />
          </>
        )}
      {isCameraSettled &&
        !productSettings.simpleDimensions &&
        showLabels &&
        coordinateData &&
        activeSetDataRef.current && activeSetDataRef.current[2] && (
          <>
            <DimensionLine
              ref={dimensionLine2Ref}
              params={params}
              line_width={line_width}
              start={activeSetDataRef.current[2].start}
              end={activeSetDataRef.current[2].end}
              visible={true}
              scaleFactor={activeSetDataRef.current[2].scaleFactor || 1}
              lineOffset={params.lineOffset}
              scaledArrowHeight={scaledArrowHeight}
              minArrowHeight={minArrowHeight}
            />
            <DimensionLabel
              ref={dimensionLabel2Ref}
              params={params}
              position={activeSetDataRef.current[2].label}
              label={activeSetDataRef.current[2].labelText}
              visible={true}
              isMobile={isMobile}
              inverse={sceneBackgroundColour === "black"}
            />
            <Arrowhead
              ref={arrowhead2StartRef}
              params={params}
              position={activeSetDataRef.current[2].start}
              direction={activeSetDataRef.current[2].end}
              visible={true}
              scaleFactor={activeSetDataRef.current[2].scaleFactor || 1}
              distanceScale={distanceScale}
              lineOffset={params.lineOffset}
              setScaledArrowHeightOut={handleScaledArrowHeightUpdate}
              minArrowHeight={minArrowHeight}
            />
            <Arrowhead
              ref={arrowhead2EndRef}
              params={params}
              position={activeSetDataRef.current[2].end}
              direction={activeSetDataRef.current[2].start}
              visible={true}
              scaleFactor={activeSetDataRef.current[2].scaleFactor || 1}
              distanceScale={distanceScale}
              lineOffset={params.lineOffset}
              setScaledArrowHeightOut={handleScaledArrowHeightUpdate}
              minArrowHeight={minArrowHeight}
            />
          </>
        )}
      
      {/* Diameters rendering */}
      {!coordinateData || !coordinateData.diameters ? null :
        coordinateData.diameters.map((line, index) => {
          return (
            <React.Fragment key={index}>
              <DimensionLine
                params={params}
                line_width={line_width}
                start={line.start}
                end={line.end}
                visible={showLabels}
                scaleFactor={coordinateData.scaleFactor}
                diameter={true}
                lineOffset={params.lineOffset}
              />
              <DimensionLabel
                params={params}
                position={coordinateData.diameterLabels[index].end}
                label={line.value}
                visible={showLabels}
                isMobile={isMobile}
                // isSmall={isSmallObject}
                inverse={sceneBackgroundColour === "black"}
                //distanceScale={distanceScale}
                offsetDirection={line.offsetDirection}
              />
            </React.Fragment>
          );
        })
      }
      {/* apartment: 'lebombo_1k.hdr',
        city: 'potsdamer_platz_1k.hdr',
        dawn: 'kiara_1_dawn_1k.hdr',
        forest: 'forest_slope_1k.hdr',
        lobby: 'st_fagans_interior_1k.hdr',
        night: 'dikhololo_night_1k.hdr',
        park: 'rooitou_park_1k.hdr',
        studio: 'studio_small_03_1k.hdr',
        sunset: 'venice_sunset_1k.hdr',
        warehouse */}

      {((productSettings.environmentIntensity && productSettings.environmentIntensity > 0) || 
      params.environmentIntensity > 0) &&
        (
          (Array.isArray(variantEnvironmentsHdr) && variantEnvironmentsHdr.length > 0 && variantEnvironmentsHdr[0]) ||
          (typeof variantEnvironmentsHdr === 'string' && variantEnvironmentsHdr.trim() !== '')
        ) && (
          <Environment
            files={
              Array.isArray(variantEnvironmentsHdr)
                ? variantEnvironmentsHdr[0]
                : variantEnvironmentsHdr
            }
            // files={
            //   `${process.env.PUBLIC_URL}/geckota_studio_env.hdr`
            // }
            // preset="warehouse"
            background={false} // can be true, false or "only" (which only sets the background) (default: false)
            environmentIntensity={productSettings.environmentIntensity || params.environmentIntensity} //{params.environmentIntensity} // optional intensity factor (default: 1, only works with three 0.163 and up)
            environmentRotation={[0, Math.PI / 2, 0]} // optional rotation (default: 0, only works with three 0.163 and up)
          />
        )
      }
      {/* <OrbitControls
            ref={controls}
        enablePan={false}
            //target={[0, 0.35, 0]} //{[0, 0.35, 0]}
            //minDistance={params.zoomClose}
            //maxDistance={params.zoomFar}
        maxPolarAngle={Math.PI / 2}
        /> */}
    </>
  );
});

const ModelViewer = ({
  variantFileGlb,
  variantFileGlbUncompressed,
  variantFileUsd,
  variantFilesHdr,
  variantEnvironmentsHdr,
  forceUpdate,
  yawAngle,
  pitchAngle,
  windowWidth,
  aspectRatio,
  objectDimensions,
  productTags,
  productSettings,
  configuratorEnabled,
  onMeshClicked,
  setCamTransform,
  camTransform,
  viewerModeState,
  //onViewerModeChange,
  parentWidth,
  parentHeight,
  windowHeight,
  onScreenshotsReady,
  modelviewerSelected
}) => {
  const store = createXRStore();
  const [isAR, setIsAR] = useState(false); // Toggle AR mode
  const [isMobile, setIsMobile] = useState(false); // Track if the device is mobile
  //TODO: Show dimensions when we switch to viewerMode to 3D

  const [showLabels, setShowLabels] = useState(productSettings.showDimensionsAtStart !== undefined && productSettings.showDimensions &&
    (!productSettings.advancedDimensions || (productSettings.advancedDimensions && objectDimensions !== undefined))
    ? productSettings.showDimensionsAtStart : productSettings.showDimensions &&
    (!productSettings.advancedDimensions || (productSettings.advancedDimensions && objectDimensions !== undefined) ? true : false)); // State for dimension labels
  const [disableLabels, setDisableLabels] = useState(false);
  const [animationPlaying, setAnimationPlaying] = useState(false);
  const [orbitControlsEnabled, setOrbitControlsEnabled] = useState(true);
  const [selectedMeshName, setSelectedMeshName] = useState(null);
  //This needs to be tied to each animation => we need an array of int states that equals the number of animations
  const [animationDirection, setAnimationDirection] = useState(-1); // 1 for forward, -1 for reverse
  const [isFirstAnimToggled, setIsFirstAnimToggled] = useState(false);
  const [isSecondAnimToggled, setIsSecondAnimToggled] = useState(false);
  const [hasAnimations, setHasAnimations] = useState(false);
  const [animationLength, setAnimationLength] = useState(0);
  const [animationsLoaded, setAnimationsLoaded] = useState(false);
  const [texture, setTexture] = useState(null); // Use state to manage the loaded texture
  const [maxViewerButtonSize, setMaxViewerButtonSize] = useState(100);
  const [lastAnimationName, setLastAnimationName] = useState(null);

  //const [yawAngleState, setYawAngleState] = useState(yawAngle);
  //const [pitchAngleState, setPitchAngleState] = useState(pitchAngle);

  const [screenshots, setScreenshots] = useState([]);
  const [screenshotInProgress, setScreenshotInProgress] = useState(false);
  const [isObjectPanning, setIsObjectPanning] = useState(false);
  const [showInfoDropdown, setShowInfoDropdown] = useState(productSettings.ThreeSixtyViewToggle || false);
  // Use ref for viewerMode to avoid unnecessary re-renders in expensive operations
  const viewerModeRef = useRef(viewerModeState || '360');
  // Keep minimal state only for UI that needs to re-render (e.g., button active state)
  const [viewerMode, setViewerMode] = useState(viewerModeState || '360');
  
  // Sync ref with state when state changes (e.g., from external updates)
  useEffect(() => {
    viewerModeRef.current = viewerMode;
  }, [viewerMode]);

  

  const controls = useRef();
  const isOrbitingRef = useRef(false);
  const touchStartRef = useRef(null);

  const getCssVariableFov = (variableName, fallback) => {
    if (typeof window === 'undefined') return fallback

    const configuratorRoot = document.getElementById('root')
    const rootStyles = window.getComputedStyle(document.documentElement)
    const configuratorStyles = configuratorRoot ? window.getComputedStyle(configuratorRoot) : null

    const rawValue =
      configuratorStyles?.getPropertyValue(variableName)?.trim() ||
      rootStyles.getPropertyValue(variableName)?.trim()

    const parsedValue = parseFloat(rawValue)

    if (Number.isFinite(parsedValue)) {
      return parsedValue
    }

    console.warn(`[FOV] CSS variable ${variableName} missing or invalid ("${rawValue}"). Using fallback ${fallback}.`)
    return fallback
  }

  const infoDropDownTopMargin = getCssVariableFov('--growm-info-dropdown-topmargin', 10);
  const infoButtonWrapperTopMargin = getCssVariableFov('--growm-info-button-wrapper-topmargin', 20); 
  const infoButtonWrapperLeftMargin = getCssVariableFov('--growm-info-button-wrapper-leftmargin', 0); 

  const toggleDimensions = () => {
    setShowLabels(!showLabels);
    // if (productSettings.pure3D && showLabels) {
    //   onViewerModeChange("360");
    // }
  }
  const toggleViewer = () => {
    const newViewer = viewerModeRef.current === '360' ? '3D' : '360';
    // Set boundsAnimating to true immediately
    // setBoundsAnimating(true);
    // Update ref immediately (no re-render)
    viewerModeRef.current = newViewer;
    // Update state only for UI that needs to re-render (button active state)
    // The spring animation will be updated via useEffect in ThreeScene using imperative API
    setViewerMode(newViewer);
    //TODO: Implement the code below in here
    // if (newViewer === SelectedViewer.ThreeD) {
    //   setYawAngle(productSettings.startCamYawAngle || 25);
    //   setPitchAngle(productSettings.startCamPitchAngle || 60);
    //   setViewerMode('3D');
    //   setIsModelViewerLoaded(true);
    // } else if (newViewer === SelectedViewer.ThreeSixty) {
    //   setPitchAngle(80);
    //   setViewerMode('360');
    // }
    //onViewerModeChange(newViewer);
  }
  const toggleAnimation = () => {
    if (productSettings.reversableAnimation) {
      if (lastAnimationName == null) {
        setAnimationDirection(-animationDirection);
        setIsFirstAnimToggled(-animationDirection > 0 ? true : false);
      }
      else {
        setAnimationDirection(1); // Reset to forward if we last played another animation
        setIsFirstAnimToggled(true);
      }
    }
    else {
      setIsFirstAnimToggled(!animationPlaying);
    }
    //We need to set animationPlaying to true if it's an exploded view
    //TODO: Either handle animationPlaying as a parameter that stays on until the bounds animation is complete
    //or set another variable for OrbitControls
    setAnimationPlaying(!animationPlaying);
    setOrbitControlsEnabled(!orbitControlsEnabled);
    setSelectedMeshName(null);
    setIsSecondAnimToggled(false);
    setShowLabels(false);
    setDisableLabels(!animationPlaying);
  }

  const toggleNamedAnimation = (meshName) => {
    if (productSettings.reversableAnimation) {
      console.log('toggleNamedAnimation: productSettings.animationMapping:', productSettings.animationMapping);
      const mapping = productSettings.animationMapping || {};
      const animName = mapping[meshName];
      if (lastAnimationName == animName) {
        setAnimationDirection(-animationDirection);
        setIsSecondAnimToggled(-animationDirection > 0 ? true : false);
      } else {
        setAnimationDirection(1); // Reset to forward if a different mesh is selected
        setIsSecondAnimToggled(true);
      }
    }
    else {
      setIsSecondAnimToggled(!animationPlaying);
    }
    //This will disable controls completely for loop animations
    setAnimationPlaying(!animationPlaying);
    setOrbitControlsEnabled(!orbitControlsEnabled);
    setIsFirstAnimToggled(false);
    setShowLabels(false);
    setDisableLabels(!animationPlaying);
  }

  const [gltfScene, setGltfScene] = useState(null);

  //AR variables
  const containerRef = useRef();
  const sceneRef = useRef();
  const canvasRef = useRef();

  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingLightMap, setLoadingLightMap] = useState(true);
  const [modalIsOpen, setIsOpen] = React.useState(false);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  //const [forceUpdate, setForceUpdate] = useState(false);

  const [showInteractionHint, setShowInteractionHint] = useState(false);
  const [enableInteractionHint, setEnableInteractionHint] = useState(true);
  const [hintAnimating, setHintAnimating] = useState(false);
  const interactionHintRef = useRef();

  const spinnerContainerRef = useRef(null); // Create a reference for the spinner container
  const spinnerInstanceRef = useRef(null); // Store the Spinner instance
  const dropdownRef = useRef();

  const [isRotationSettled, setIsRotationSettled] = useState(true);
  
  // Scroll position tracking for smooth camera movement
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimeoutRef = useRef(null);
  const scrollMomentumRef = useRef(0);
  const lastScrollTimeRef = useRef(0);
  const lastScrollEventRef = useRef(0);
  const scrollResetTimeoutRef = useRef(null);

  // Create refs to store camera and scroll handlers
  const cameraRef = useRef();
  const scrollHandlersRef = useRef({});
  const gestureHandlerRef = useRef(null);
  const canvasElementRef = useRef(null);
  
  // Detect if device supports touch
  const isTouchDevice = useMemo(() => {
    // More robust touch detection
    const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const isTablet = /iPad|Android/i.test(navigator.userAgent) && 'ontouchstart' in window;
    
    const isTouch = hasTouch && (isMobile || isTablet);
    console.log('Device detection:', { 
      isTouch, 
      hasOntouchstart: 'ontouchstart' in window, 
      maxTouchPoints: navigator.maxTouchPoints,
      isMobile,
      isTablet,
      userAgent: navigator.userAgent
    });
    return isTouch;
  }, []);

  const [controlsEnabled, setControlsEnabled] = useState(!isTouchDevice);
  
  // Initialize gesture handler only for touch devices
  useEffect(() => {
    console.log('Gesture handler effect running, isTouchDevice:', isTouchDevice);
    if (!isTouchDevice) {
      console.log('Not a touch device, skipping gesture handler initialization');
      return;
    }
    
    const canvasWrapper = document.querySelector('.canvas-wrapper');
    if (canvasWrapper && !gestureHandlerRef.current) {
      console.log('Initializing TouchGestureHandler for touch device');
      gestureHandlerRef.current = new TouchGestureHandler(canvasWrapper, controls, canvasElementRef, setControlsEnabled, setEnableInteractionHint);
      
      // Ensure canvas-wrapper is always ready to receive events
      canvasWrapper.style.touchAction = 'pan-y';
      canvasWrapper.style.pointerEvents = 'auto';
      
      console.log('TouchGestureHandler initialized with canvas-wrapper for touch device');
    }
    
    return () => {
      if (gestureHandlerRef.current) {
        console.log('Destroying TouchGestureHandler');
        gestureHandlerRef.current.destroy();
        gestureHandlerRef.current = null;
      }
    };
  }, [controls, setControlsEnabled, isTouchDevice]);
  
  // Store canvas element reference when available
  useEffect(() => {
    if (canvasRef.current) {
      canvasElementRef.current = canvasRef.current;
    }
  }, [canvasRef]);
  
  // Add wheel event handler to canvas-wrapper for zooming (touch devices only)
  useEffect(() => {
    if (!isTouchDevice) return;
    
    const canvasWrapper = document.querySelector('.canvas-wrapper');
    if (canvasWrapper) {
      const handleWheel = (event) => {
        // Check if mouse is over the canvas area
        const canvas = canvasRef.current;
        if (canvas) {
          const rect = canvas.getBoundingClientRect();
          const x = event.clientX;
          const y = event.clientY;
          
          // If mouse is over canvas, prevent scrolling and enable zoom
          if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
            event.preventDefault();
            
            // Let OrbitControls handle the zoom
            if (controls.current) {
              controls.current.enabled = true;
              // Create synthetic wheel event for OrbitControls
              const syntheticEvent = new WheelEvent('wheel', {
                deltaY: event.deltaY,
                clientX: event.clientX,
                clientY: event.clientY,
                bubbles: true,
                cancelable: true,
                view: window
              });
              canvas.dispatchEvent(syntheticEvent);
            }
          }
        }
      };
      
      canvasWrapper.addEventListener('wheel', handleWheel, { passive: false });
      
      return () => {
        canvasWrapper.removeEventListener('wheel', handleWheel);
      };
    }
  }, [canvasRef, controls, isTouchDevice]);
  
  // Reinitialize canvas-wrapper after scrolling to ensure it stays active (touch devices only)
  useEffect(() => {
    if (!isTouchDevice) return;
    
    const handleScroll = () => {
      const canvasWrapper = document.querySelector('.canvas-wrapper');
      if (canvasWrapper) {
        // Ensure canvas-wrapper is still ready to receive events after scrolling
        canvasWrapper.style.touchAction = 'pan-y';
        canvasWrapper.style.pointerEvents = 'auto';
        console.log('Canvas-wrapper reinitialized after scroll');
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isTouchDevice]);
  
  // Add global touch event handlers as backup (touch devices only)
  useEffect(() => {
    if (!isTouchDevice) return;
    
    const handleGlobalTouchStart = (event) => {
      console.log('Global touch start detected');
    };
    
    const handleGlobalTouchMove = (event) => {
      console.log('Global touch move detected');
    };
    
    document.addEventListener('touchstart', handleGlobalTouchStart, { passive: true });
    document.addEventListener('touchmove', handleGlobalTouchMove, { passive: true });
    
    return () => {
      document.removeEventListener('touchstart', handleGlobalTouchStart);
      document.removeEventListener('touchmove', handleGlobalTouchMove);
    };
  }, [isTouchDevice]);
  
  
  // TouchGestureHandler class for handling scroll vs rotate gestures
  class TouchGestureHandler {
    constructor(element, controlsRef, canvasRef, setControlsEnabled, setEnableInteractionHint) {
      this.touchDecided = false;
      this.touchMode = null; // 'rotate' | 'scroll' | null
      this.startPosition = { x: 0, y: 0 };
      this.element = element;
      this.controlsRef = controlsRef;
      this.canvasRef = canvasRef;
      this.setControlsEnabled = setControlsEnabled;
      this.setEnableInteractionHint = setEnableInteractionHint;
      this.threshold = 2; // Minimum movement to decide gesture
      this.isRotating = false;
      this.lastPosition = { x: 0, y: 0 };
      this.controlsEnabledRef = { current: false }; // Track enabled state immediately
      this.touchCount = 0; // Track number of active touches
      this.gestureStartTime = 0; // Track when gesture started
      this.multiTouchDelay = 100; // Delay before deciding gesture to allow multi-touch detection
      this.initialTouchDistance = 0; // Initial distance between touches for zoom
      this.lastTouchDistance = 0; // Last distance between touches for zoom
      
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onTouchStart = this.onTouchStart.bind(this);
      this.onTouchMove = this.onTouchMove.bind(this);
      this.onTouchEnd = this.onTouchEnd.bind(this);
      
      this.element.addEventListener('pointerdown', this.onPointerDown);
      this.element.addEventListener('pointermove', this.onPointerMove);
      this.element.addEventListener('pointerup', this.onPointerUp);
      this.element.addEventListener('touchstart', this.onTouchStart);
      this.element.addEventListener('touchmove', this.onTouchMove);
      this.element.addEventListener('touchend', this.onTouchEnd);
    }

    onPointerDown(event) {
      console.log('Pointer down received:', event.pointerType, 'Primary:', event.isPrimary, 'Touch count:', this.touchCount);
      
      this.gestureStartTime = Date.now();
      
      // Check if this is a multi-touch gesture (pinch to zoom)
      if (event.pointerType === 'touch' && event.isPrimary === false) {
        // This is a secondary touch - let OrbitControls handle it
        console.log('Secondary touch detected - letting OrbitControls handle');
        return;
      }
      
      // If we have multiple touches, let OrbitControls handle everything
      if (this.touchCount > 1) {
        console.log('Multi-touch detected - letting OrbitControls handle');
        return;
      }
      
      this.touchDecided = false;
      this.touchMode = null;
      this.isRotating = false;
      this.startPosition.x = event.clientX;
      this.startPosition.y = event.clientY;
      this.lastPosition.x = event.clientX;
      this.lastPosition.y = event.clientY;
      
      console.log('Pointer down at:', this.startPosition);
    }

    onPointerMove(event) {
      console.log('Pointer move received:', event.pointerType, 'Primary:', event.isPrimary, 'Touch decided:', this.touchDecided);
      
      // Check if this is a multi-touch gesture (pinch to zoom)
      if (event.pointerType === 'touch' && event.isPrimary === false) {
        // This is a secondary touch - let OrbitControls handle it
        return;
      }
      
      // If we have multiple touches, let OrbitControls handle everything
      if (this.touchCount > 1) {
        return;
      }
      
      // Add delay to allow multi-touch detection
      const timeSinceStart = Date.now() - this.gestureStartTime;
      if (timeSinceStart < this.multiTouchDelay) {
        return; // Wait for potential multi-touch
      }
      
      if (!this.touchDecided) {
        const dx = event.clientX - this.startPosition.x;
        const dy = event.clientY - this.startPosition.y;
        const dxMag = Math.abs(dx);
        const dyMag = Math.abs(dy);
        
        // Only decide after minimum movement threshold
        if (dxMag > this.threshold || dyMag > this.threshold) {
          this.touchDecided = true;
          console.log('Gesture detected - dx:', dxMag, 'dy:', dyMag, 'threshold:', this.threshold);
          
          // The key decision logic - bias toward rotation
          // Require vertical movement to be significantly larger than horizontal to trigger scroll
          const verticalBias = 4.0; // Higher values make scrolling harder to trigger
          if (dyMag > dxMag * verticalBias) {
            // Vertical gesture - let page scroll
            this.touchMode = 'scroll';
            console.log('Scroll gesture detected - dy:', dyMag, 'dx:', dxMag, 'bias:', verticalBias);
            return; // Don't prevent default, don't add listeners
          } else {
            // Horizontal gesture or mixed gesture - capture for rotation
            this.touchMode = 'rotate';
            this.isRotating = true;
            console.log('Rotate gesture detected - dy:', dyMag, 'dx:', dxMag, 'bias:', verticalBias);
            
            // Prevent scrolling immediately when rotation is detected
            event.preventDefault();
            
            // Change wrapper touch action to prevent scrolling
            this.element.style.touchAction = 'none';
            console.log('Wrapper touch action changed to none for rotation');
            
            // Disable interaction hint when user starts interacting
            if (this.setEnableInteractionHint) {
              this.setEnableInteractionHint(false);
            }
            
            // Enable OrbitControls
            if (this.controlsRef.current) {
              this.controlsRef.current.enabled = true;
            }
            
            // Set both ref (immediate) and state (for next render)
            this.controlsEnabledRef.current = true;
            this.setControlsEnabled(true);
            
            // Set lastPosition to current position for proper rotation calculation
            this.lastPosition.x = event.clientX;
            this.lastPosition.y = event.clientY;
            
            // Skip rotation processing for this call since we just detected the gesture
            return;
          }
        }
      }

      if (this.touchMode === 'rotate' && this.isRotating) {
        console.log('Processing rotation - preventing default');
        // Prevent scrolling during rotation
        //Is this active straight away or only after the next pointer up event?
        event.preventDefault();
        
        // Manually trigger OrbitControls rotation
        this.triggerOrbitControlsRotation(event);
      }
    }

    triggerOrbitControlsRotation(event) {
      console.log('triggerOrbitControlsRotation called');
      if (!this.controlsRef.current || !this.canvasRef.current) return;
      
      const controls = this.controlsRef.current;
      const canvas = this.canvasRef.current;
      
      // Calculate movement delta
      const deltaX = event.clientX - this.lastPosition.x;
      const deltaY = event.clientY - this.lastPosition.y;
      
      // Update last position
      this.lastPosition.x = event.clientX;
      this.lastPosition.y = event.clientY;
      
      // Convert pixel movement to rotation angles
      const rotationSpeed = 0.02; // Much higher sensitivity for faster rotation
      const azimuthAngle = -deltaX * rotationSpeed;
      const polarAngle = -deltaY * rotationSpeed;
      
      // Get current camera position and target
      const camera = controls.object;
      const target = controls.target;
      
      // Create spherical coordinates
      const spherical = new THREE.Spherical();
      spherical.setFromVector3(camera.position.clone().sub(target));
      
      // Update angles
      spherical.theta += azimuthAngle;
      spherical.phi += polarAngle;
      
      // Clamp polar angle to prevent flipping
      spherical.phi = Math.max(0.1, Math.min(Math.PI - 0.1, spherical.phi));
      
      // Update camera position
      camera.position.setFromSpherical(spherical).add(target);
      
      // Update controls
      controls.update();
    }

    dispatchMouseEvent(eventType, originalEvent) {
      if (!this.controlsRef.current || !this.canvasRef.current) return;
      
      const canvas = this.canvasRef.current;
      
      // Create synthetic mouse event
      const syntheticEvent = new MouseEvent(eventType, {
        clientX: originalEvent.clientX,
        clientY: originalEvent.clientY,
        bubbles: true,
        cancelable: true,
        view: window
      });
      
      // Dispatch the synthetic event to the canvas (OrbitControls listens to canvas events)
      canvas.dispatchEvent(syntheticEvent);
    }

    onTouchStart(event) {
      console.log('Touch start - actual touch count:', event.touches.length);
      this.touchCount = event.touches.length;
      
      // If we have multiple touches, enable OrbitControls for pinch-to-zoom
      if (this.touchCount > 1) {
        console.log('Multi-touch detected - enabling OrbitControls for pinch-to-zoom');
        
        // Disable interaction hint when user starts interacting
        if (this.setEnableInteractionHint) {
          this.setEnableInteractionHint(false);
        }
        
        // Enable OrbitControls
        if (this.controlsRef.current) {
          this.controlsRef.current.enabled = true;
        }
        this.controlsEnabledRef.current = true;
        this.setControlsEnabled(true);
        
        // Stop our gesture detection for multi-touch
        this.touchDecided = true;
        this.touchMode = 'zoom';
        
        // Prevent wrapper from handling events - we'll handle zoom ourselves
        event.preventDefault();
        console.log('Multi-touch: starting custom zoom handling');
        
        // Store initial touch positions for zoom calculation
        this.initialTouchDistance = this.getTouchDistance(event.touches);
        this.lastTouchDistance = this.initialTouchDistance;
      }
    }

    onTouchEnd(event) {
      console.log('Touch end - actual touch count:', event.touches.length);
      this.touchCount = event.touches.length;
      
      // If this is multi-touch, handle zoom end
      if (this.touchMode === 'zoom' && this.touchCount > 0) {
        event.preventDefault();
        console.log('Multi-touch end: continuing zoom handling');
      }
      
      // If no more touches, clean up
      if (this.touchCount === 0) {
        console.log('All touches ended - cleaning up');
        this.touchMode = null;
        this.touchDecided = false;
        this.isRotating = false;
        
        // Reset wrapper touch action to allow scrolling
        this.element.style.touchAction = 'pan-y';
        console.log('Wrapper touch action reset to pan-y');
        
        // Disable OrbitControls after all touches end
        if (this.controlsRef.current) {
          this.controlsRef.current.enabled = false;
        }
        this.controlsEnabledRef.current = false;
        this.setControlsEnabled(false);
      }
    }

    onTouchMove(event) {
      // If this is multi-touch, handle custom zoom
      if (this.touchMode === 'zoom') {
        event.preventDefault();
        
        if (event.touches.length >= 2) {
          const currentDistance = this.getTouchDistance(event.touches);
          const distanceChange = currentDistance - this.lastTouchDistance;
          
          // Apply zoom based on distance change
          this.handleZoom(distanceChange);
          
          this.lastTouchDistance = currentDistance;
          console.log('Custom zoom applied:', distanceChange);
        }
      }
      
      // If this is rotation, prevent scrolling
      if (this.touchMode === 'rotate') {
        event.preventDefault();
        console.log('Touch move - preventing scroll during rotation');
      }
    }

    onPointerUp(event) {
      console.log('Pointer up received - touch count:', this.touchCount, 'touch decided:', this.touchDecided);
      
      // Don't decrement touch count here - let onTouchEnd handle it
      
      // Check if this is a multi-touch gesture (pinch to zoom)
      if (event.pointerType === 'touch' && event.isPrimary === false) {
        // This is a secondary touch - let OrbitControls handle it
        return;
      }
      
      // Only clean up if we have no touches left (handled by onTouchEnd)
      if (this.touchCount === 0) {
        console.log('Pointer up - cleaning up (no touches left)');
        
        // Always reset state when touch ends, regardless of touch count
        this.touchMode = null;
        this.touchDecided = false;
        this.isRotating = false;
        
        // Reset wrapper touch action to allow scrolling
        this.element.style.touchAction = 'pan-y';
        console.log('Pointer up - wrapper touch action reset to pan-y');
        
        // Don't change pointerEvents - it's ignored during active touch
        // Canvas stays in its initial state
        
        // Disable OrbitControls after touch ends
        if (this.controlsRef.current) {
          this.controlsRef.current.enabled = false;
        }
        
        // Set both ref (immediate) and state (for next render)
        this.controlsEnabledRef.current = false;
        this.setControlsEnabled(false);
      }
    }

    getTouchDistance(touches) {
      if (touches.length < 2) return 0;
      
      const touch1 = touches[0];
      const touch2 = touches[1];
      
      const dx = touch1.clientX - touch2.clientX;
      const dy = touch1.clientY - touch2.clientY;
      
      return Math.sqrt(dx * dx + dy * dy);
    }

    handleZoom(distanceChange) {
      if (!this.controlsRef.current) return;
      
      const controls = this.controlsRef.current;
      const zoomSpeed = 0.01; // Adjust sensitivity
      
      // Calculate zoom factor based on distance change
      const zoomFactor = 1 + (distanceChange * zoomSpeed);
      
      // Apply zoom by adjusting camera distance
      const currentDistance = controls.object.position.distanceTo(controls.target);
      const newDistance = currentDistance / zoomFactor;
      
      // Apply zoom limits if they exist
      const minDistance = controls.minDistance || 0.1;
      const maxDistance = controls.maxDistance || 1000;
      
      const clampedDistance = Math.max(minDistance, Math.min(maxDistance, newDistance));
      
      // Update camera position
      const direction = controls.object.position.clone().sub(controls.target).normalize();
      controls.object.position.copy(controls.target).add(direction.multiplyScalar(clampedDistance));
      
      controls.update();
    }

    destroy() {
      this.element.removeEventListener('pointerdown', this.onPointerDown);
      this.element.removeEventListener('pointermove', this.onPointerMove);
      this.element.removeEventListener('pointerup', this.onPointerUp);
      this.element.removeEventListener('touchstart', this.onTouchStart);
      this.element.removeEventListener('touchmove', this.onTouchMove);
      this.element.removeEventListener('touchend', this.onTouchEnd);
    }
  }

    //Show options if we have dimensions to show and one animation or if we've got several animations
  const showOptions = (((productSettings.showDimensions === true &&
    ((!productSettings.advancedDimensions && !productSettings.simpleDimensions) || ((productSettings.advancedDimensions || productSettings.simpleDimensions)
      && objectDimensions !== undefined && objectDimensions !== ""))) && (hasAnimations))
    || (hasAnimations && productSettings.animationMapping && Object.keys(productSettings.animationMapping).length > 1));

    console.log('show options:', showOptions, productSettings.advancedDimensions, productSettings.simpleDimensions, hasAnimations);

  useEffect(() => {
    setShowInfoDropdown(!showOptions);
  }, [showOptions]);

  // Dropdown animation
  useEffect(() => {
    console.log('show info drop down changed to:', showInfoDropdown);
    if (!showOptions) return;
    if (dropdownRef.current) {
      if (showInfoDropdown) {
        console.log('set drop down visible to true');
        setDropdownVisible(true);
        gsap.fromTo(dropdownRef.current,
          { opacity: 0, y: -20, scaleY: 0 },
          { opacity: 1, y: 0, scaleY: 1, duration: 0.3, ease: "power2.out" }
        );
      }
      else {
        console.log('set drop down visible to false');
        gsap.fromTo(dropdownRef.current,
          { opacity: 1, y: 0, scaleY: 1 },
          { opacity: 0, y: -20, scaleY: 0, duration: 0.3, ease: "power2.out", onComplete: () => setDropdownVisible(false) }
        );
      }
    }
  }, [showInfoDropdown]);

  const takeScreenshots = useCallback(async () => {
    if (!gltfScene || screenshotInProgress) return;

    setScreenshotInProgress(true);
    const angles = [0, 90, 180, 270];
    const capturedScreenshots = [];

    // Get current camera position to restore later
    const originalYaw = yawAngle;
    const originalPitch = pitchAngle;

    // Get canvas and renderer from ThreeScene
    const canvas = canvasRef?.current;
    if (!canvas) {
      console.log('no canvas');
      setScreenshotInProgress(false);
      return;
    }
    // Get the main renderer from the canvas
    const gl = canvas.getContext('webgl2') || canvas.getContext('webgl');
    if (!gl) {
      setScreenshotInProgress(false);
      return;
    }
    // Create scene setup similar to ThreeScene
    const tempScene = new THREE.Scene();
    tempScene.background = new THREE.Color(0xffffff);

    // Clone the GLTF scene
    const clonedModel = gltfScene.clone();
    tempScene.add(clonedModel);

    // Add lights (copy from your ThreeScene lighting setup)
    const ambientLight = new THREE.AmbientLight(0xffffff, 1.5);
    tempScene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 1.5);
    directionalLight.position.set(0, 2, 1);
    tempScene.add(directionalLight);

    // Create temporary renderer for off-screen rendering
    const tempRenderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: false,
      preserveDrawingBuffer: true
    });
    //tempRenderer.setSize(canvas.width, canvas.height);
    tempRenderer.setSize(512, 512); // Fixed size for thumbnails
    tempRenderer.shadowMap.enabled = true;
    tempRenderer.shadowMap.type = THREE.PCFSoftShadowMap;
    tempRenderer.outputColorSpace = THREE.SRGBColorSpace;

    // Create temporary camera
    //const tempCamera = new THREE.PerspectiveCamera(35, canvas.width / canvas.height, 0.1, 1000);
    const tempCamera = new THREE.PerspectiveCamera(35, 1, 0.1, 1000);

    // Get scene center for camera positioning
    const box = computeScaledBoundingBox(clonedModel);
    //const box = new THREE.Box3().setFromObject(clonedModel);
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    const distance = Math.max(size.x, size.z) * 2;
    console.log('box size, distance:', size, distance);

    for (const angle of angles) {
      // Position temp camera instantly
      //Convert degree to point of view
      const yawRad = THREE.MathUtils.degToRad(angle);

      const pointOfView = angle === 0 ? 'front' :
        angle === 90 ? 'left' :
          angle === 180 ? 'back' :
            angle === 270 ? 'right' : null;

      const pitchRad = THREE.MathUtils.degToRad(90);

      tempCamera.position.setFromSphericalCoords(distance, pitchRad, yawRad);
      tempCamera.position.add(center);
      tempCamera.lookAt(center);
      tempCamera.updateMatrixWorld();

      // Render scene with temp camera
      tempRenderer.render(tempScene, tempCamera);

      // Capture screenshot
      const dataURL = tempRenderer.domElement.toDataURL('image/png', 0.9);
      const blob = await new Promise(resolve =>
        tempRenderer.domElement.toBlob(resolve, 'image/png', 0.9)
      );

      //Map matching normal point of view screenshots, where the key is 'front', 'left', etc.
      capturedScreenshots[pointOfView] = { dataURL, blob };
    }

    // Cleanup 
    tempRenderer.dispose();
    tempScene.clear();

    setScreenshots(capturedScreenshots);
    setScreenshotInProgress(false);

    // Call parent callback
    if (onScreenshotsReady) {
      onScreenshotsReady(capturedScreenshots);
      console.log('captured screenshots:', capturedScreenshots);
    }
  }, [gltfScene, onScreenshotsReady, yawAngle, pitchAngle]);

  // Trigger screenshots on first load
  // Use polling to check viewerModeRef without causing re-renders
  useEffect(() => {
    if (!productSettings.pure3D || !gltfScene || screenshotInProgress || screenshots.length > 0 || !productSettings.captureScreenshots) {
      return;
    }
    
    const checkAndTakeScreenshots = () => {
      const currentViewerMode = viewerModeRef.current;
      console.log('viewerMode, pure3D, gltfScene, screenshotInProgress, screenshots:',
        currentViewerMode, productSettings.pure3D, gltfScene, screenshotInProgress, screenshots);
      if (productSettings.ThreeSixtyViewer >= 0 && currentViewerMode === '360') {
        takeScreenshots();
      }
    };
    
    checkAndTakeScreenshots();
    const interval = setInterval(checkAndTakeScreenshots, 500);
    return () => clearInterval(interval);
  }, [productSettings.pure3D, productSettings.ThreeSixtyViewer, productSettings.captureScreenshots, gltfScene, screenshotInProgress, screenshots.length]);


  // Only set `isMobile` once when the component mounts
  useEffect(() => {
    setIsMobile(/Mobi|Android/i.test(navigator.userAgent));
  }, []); // Empty dependency array ensures this runs only on mounts

  useEffect(() => {
    console.log('variantFileGlb changed, forcing update:', variantFileGlb);
    setLoadingLightMap(true);
  }, [variantFileGlb]);

  const getRootUrl = () => {
    const { protocol, host } = window.location; // Extract protocol (http/https) and host
    return `${protocol}//${host}`; // Combine them to form the root URL
  };

  useEffect(() => {
    // Prepare the iOS and Android links with scaling disabled for iOS
    const iosLink = `${variantFileUsd}#allowsContentScaling=0`;
    const androidLink = variantFileGlbUncompressed;
    //Get the website root url
    const landingPageUrl =
      getRootUrl() +
      `/ar-redirect/?iosLink=${encodeURIComponent(
        iosLink
      )}&androidLink=${encodeURIComponent(androidLink)}`;
    console.log("Landing page url:", landingPageUrl);

    // Generate QR Code for the landing page
    QRCode.toDataURL(landingPageUrl, { width: 300 })
      .then((url) => setQrCodeUrl(url))
      .catch((err) => console.error("QR Code generation failed:", err));
  }, []);

  useEffect(() => {
    console.log('ModelViewer.js: productSettings:', productSettings);
    //setProductSettings(productSettings);
  }, [productSettings]);

  useEffect(() => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;

    const isAndroidDevice = /android/i.test(userAgent);
    const isiOSDevice = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
    const isMobileDevice = /Mobi|Android/i.test(userAgent);

    setIsAndroid(isAndroidDevice);
    setIsIOS(isiOSDevice);
    setIsMobile(isMobileDevice);
    setLoading(false); // Set loading to false after setting states
  }, []); // Run only on mount

  const params = useRef({
    //Canvas
    canvas_width: window.innerWidth,
    canvas_height: window.innerHeight,
    zoomClose: 0.25,
    zoomFar: 1,
    minZoomClose: 0.1,
    maxZoomClose: 2,
    maxZoomFar: 20,
    maxZoomFactor: 2,
    zoomOnPartMultiplier: 0.55,
    zoomMultiplier: 0.5,
    cameraOffset_desktop: 2,
    cameraOffset_mobile: 2,
    cameraFOV_desktop: 45,
    cameraFOV_mobile: 45,
    smallObjectScaleUp: 1,

    //Model
    //modelName: 'home_furniture_chair_tub',
    //modelName: 'home_furniture_sofa_BA_swift',
    //modelName: variantFileGlb[0], //It comes out as an array of length 1
    //modelName: 'home_furniture_sofa_signature.glb',
    //modelName: 'home_furniture_chair_tub.glb',
    //modelName: 'home_furniture_cabinet_animated',
    //modelName: 'electronics_kitchenaid_foodmixer.glb',
    //modelName: 'style_shoe_trainer_black.glb',
    //modelName: 'EARRING4-B.glb',
    //modelName: 'EARRING2.glb',
    //modelName: 'hex_bolt.glb',
    //modelName: "demo_screw_1.glb",
    modelName: "geckota_octopod_model5.glb",

    //Shadows
    shadowmapsize: 2048,
    shadowblurradius: 10,
    shadowblursamples: 15,
    shadowbias: -0.001,
    shadowNear: 1.5,
    shadowFar: 100,

    //Lights
    lightAngle: Math.PI / 5,
    lightRadius: 1.5,
    lightheight: 2.5,
    spotpenumbra: 0.5,
    spotdecay: 2,
    amblight_colour: 0xffffff,
    amblight_intensity: 0.15,
    dirlightIntensity: 0.8,
    lightMapIntensity: 3.5,
    bakedAmblight_colour: 0xffffff,
    bakedAmblight_intensity: 5,
    lights_colour: 0xffffff,
    lights_intensity: 4,
    spotLightOn: false, // Toggle for spotlight visibility
    spot_intensity: 10,
    showLabelsOn: false,

    //Environment
    floor_colour: 0xffffff,
    background_colour: 0xffffff,
    environmentIntensity: 1.75, //1.75, 0.3

    //Controls
    light_position: 7,
    helpers: false,
    enablePan: false,
    fullAngle: false,

    //Lines
    line_model_offset: 0.02,
    line_floor_offset: 0.01,
    arrow_head_offset: 0.02,
    line_colour: productSettings.labelColour || (productSettings.sceneBackgroundColour === "black" ? "#ffffff" : "#000000"),
    line_width_desktop: 4,
    line_width_mobile: 2,
    line_opacity: 0.5,
    cone_opacity: 0.7,
    lineOffset: 1.5,

    //Labels
    labelColour: productSettings.labelColour ? parseInt(productSettings.labelColour.replace('#', '0x')) : (productSettings.sceneBackgroundColour === "black" ? 0xffffff : 0x000000),

    //Rendering Effects
    bloom_threshold: 0,
    bloom_strength: 1,
    bloom_radius: 0.5,
    saoBias: 0.5, // Increased for better effect
    saoIntensity: 0.07,
    saoScale: 0.8,
    saoKernelRadius: 1,
    saoBlur: true,
    saoBlurRadius: 10,
    saoBlurStdDev: 5,
    saoBlurDepthCutoff: 0.01,

    //OS
    isAndroid: isAndroid,
  });

  // Update the params object when isAndroid changes
  useEffect(() => {
    params.current.isAndroid = isAndroid;
  }, [isAndroid]); // Runs every time isAndroid changes

  useEffect(() => {
    console.log("product tags modelviewer:", productTags);
    params.current.line_colour = productSettings.dimLineColour ? parseInt(productSettings.dimLineColour.replace('#', '0x')) :
      (productSettings.sceneBackgroundColour === "black" ? 0xffffff : 0x000000);
    params.current.labelColour = productSettings.labelColour ||
      (productSettings.sceneBackgroundColour === "black" ? "#ffffff" : "#000000");
    if (productTags.includes("jewellery")) {
      params.current.modelScale = 10;
      params.current.environmentIntensity = 1;
      params.current.enablePan = true;
      params.current.fullAngle = true;
    } else {
      //Non exclusive if statements so we can accept several tags, glass and reflective override fabrics
      if (productTags.includes("fabrics")) {
        console.log("fabrics");
        params.current.environmentIntensity = 0;
      }
      if (productTags.includes("reflective")) {
        console.log("reflective");
        params.current.environmentIntensity = 1;
      }
      if (productTags.includes("glass")) {
        console.log("glass");
        params.current.environmentIntensity = 1;
        params.current.enablePan = true;
        params.current.fullAngle = true;
      }
      if (productTags.includes("freeCam")) {
        console.log("free cam");
        params.current.enablePan = true;
        params.current.fullAngle = true;
      }
      if (productTags.includes("smallObject")) {
        params.current.environmentIntensity = 0.75;
      }
    }
    if (productSettings.fullAngle) {
      params.current.fullAngle = productSettings.fullAngle;
    }
  }, [productTags, productSettings]);

  const cameraOffset = isMobile
    ? params.current.cameraOffset_mobile
    : params.current.cameraOffset_desktop;
  const cameraFOV = isMobile
    ? params.current.cameraFOV_mobile
    : params.current.cameraFOV_desktop;
  const line_width = isMobile
    ? params.current.line_width_mobile
    : params.current.line_width_desktop;

  const triggerAR = async () => {
    if (
      navigator.xr &&
      (await navigator.xr.isSessionSupported("immersive-ar"))
    ) {
      //const session = await navigator.xr.requestSession('immersive-ar', { requiredFeatures: ['hit-test'] });
      const session = await navigator.xr.requestSession("immersive-ar", {
        requiredFeatures: ["hit-test"],
      });

      const renderer = new THREE.WebGLRenderer({ alpha: true });
      renderer.setSize(window.innerWidth, window.innerHeight);
      renderer.xr.enabled = true;
      document.body.appendChild(renderer.domElement);

      if (
        containerRef.current &&
        !containerRef.current.contains(renderer.domElement)
      ) {
        containerRef.current.appendChild(renderer.domElement);
      }
      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(
        70,
        window.innerWidth / window.innerHeight,
        0.01,
        20
      );
      const light = new THREE.HemisphereLight(0xffffff, 0xbbbbff, 1);
      scene.add(light);

      // Load the GLTF model and add it to the scene
      const loader = new GLTFLoader();
      const dracoLoader = new DRACOLoader();
      dracoLoader.setDecoderPath(
        "https://cdn.jsdelivr.net/npm/three@0.153.0/examples/jsm/libs/draco/gltf/"
      );
      loader.setDRACOLoader(dracoLoader);
      let model;

      loader.load(
        variantFileGlb[0],
        (gltf) => {
          model = gltf.scene;
          model.scale.set(1, 1, 1);
          model.position.set(0, 0, -2);
          sceneRef.current.add(model);
        },
        undefined,
        (error) => {
          console.error("Error loading GLTF model:", error);
        }
      );

      // Request reference space
      let referenceSpace = null;
      const spacesToTry = ["local-floor", "viewer", "local"];

      for (const space of spacesToTry) {
        try {
          referenceSpace = await session.requestReferenceSpace(space);
          if (referenceSpace) break; // Exit loop once a valid space is found
        } catch (error) {
          console.warn(
            `Reference space ${space} not supported. Trying next...`
          );
        }
      }

      if (!referenceSpace) {
        console.error("No compatible reference space found.");
        return;
      }

      // Ensure renderer uses the correct reference space
      renderer.xr.setReferenceSpace(referenceSpace);
      console.log("Session:", session);
      renderer.xr.setSession(session);

      session.addEventListener("end", () => {
        renderer.setAnimationLoop(null);
      });

      renderer.setAnimationLoop(() => {
        renderer.render(scene, camera);
      });
    } else {
      console.error("WebXR not supported");
    }
  };

  const customStyles = {
    content: {
      top: "50%",
      left: "50%",
      right: "auto",
      bottom: "auto",
      marginRight: "-50%",
      transform: "translate(-50%, -50%)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      padding: "20px",
      maxWidth: "90%", // Ensure it fits smaller screens
      maxHeight: "90%", // Prevent overflow on smaller screens
      boxSizing: "border-box",
    },
    overlay: {
      display: "flex", // Ensure the modal centers within the overlay
      justifyContent: "center",
      alignItems: "center",
      backgroundColor: "rgba(0, 0, 0, 0.5)", // Dimmed background for better focus
      zIndex: 1000,
    },
  };

  function openModal() {
    setShowLabels(false);
    setIsOpen(true);
  }

  function afterOpenModal() {
    // Code placeholder
  }

  function closeModal() {
    setIsOpen(false);
  }

  useEffect(() => {
    // Spinner options
    const spinnerOptions = {
      lines: 12, // The number of lines to draw
      length: 0, // The length of each line
      width: 5, // The line thickness
      radius: 10, // The radius of the inner circle
      scale: 4, // Scales overall size
      corners: 1, // Corner roundness (0..1)
      color: '#000', // CSS color or array of colors
      fadeColor: 'transparent', // CSS color or array of colors
      speed: 1, // Rounds per second
      rotate: 0, // The rotation offset
      animation: 'spinner-line-fade-quick', // The CSS animation name
      direction: 1, // 1: clockwise, -1: counterclockwise
      zIndex: 2e9, // The z-index (defaults to 2000000000)
      className: 'spinner', // CSS class to assign to the spinner
      top: '50%', // Top position relative to parent
      left: '50%', // Left position relative to parent
      shadow: '0 0 1px transparent', // Box-shadow for the lines
      position: 'absolute', // Element positioning
    };


    // Create a Spinner instance
    const spinner = new Spinner(spinnerOptions);
    spinnerInstanceRef.current = spinner;

    // Mount spinner to the container
    if (spinnerContainerRef.current) {
      spinner.spin(spinnerContainerRef.current);
    }

    // Cleanup on component unmount
    return () => {
      if (spinnerInstanceRef.current) {
        spinnerInstanceRef.current.stop();
      }
    };
  }, [spinnerContainerRef.current]);

  //Switch off dimensions when switching viewer mode (only when using viewpoint buttons)
  // Use polling to check viewerModeRef without causing re-renders
  useEffect(() => {
    const checkViewerMode = () => {
      if (viewerModeRef.current === '360') {
        setShowLabels(false);
      }
    };
    
    checkViewerMode();
    const interval = setInterval(checkViewerMode, 100);
    return () => clearInterval(interval);
  }, []);

  //Clean up of empty divs
  useEffect(() => {
    const cleanupEmptyDivs = () => {
      const emptyDivs = document.querySelectorAll('div[style*="display: none"]');
      emptyDivs.forEach(div => {
        if (div.innerHTML.trim() === '' || div.children.length === 0) {
          div.remove();
        }
      });
    };

    // Run cleanup on component mount and updates
    cleanupEmptyDivs();

    // Set up interval to periodically clean up
    const cleanupInterval = setInterval(cleanupEmptyDivs, 1000);

    // Cleanup on unmount
    return () => {
      clearInterval(cleanupInterval);
      cleanupEmptyDivs();
    };
  }, []);

  // Also add cleanup when component unmounts
  useEffect(() => {
    return () => {
      // Clean up any remaining empty divs when component unmounts
      const emptyDivs = document.querySelectorAll('div[style*="display: none"]');
      emptyDivs.forEach(div => {
        if (div.innerHTML.trim() === '' || div.children.length === 0) {
          div.remove();
        }
      });
    };
  }, []);


  // Interaction hint animation
  useEffect(() => {
    if (!gltfScene || !enableInteractionHint || modelviewerSelected !== SelectedViewer.ThreeD) {
      gsap.killTweensOf(interactionHintRef.current);
      setShowInteractionHint(false);
      setHintAnimating(false);
      if (!gltfScene || modelviewerSelected !== SelectedViewer.ThreeD) {
        setIsSecondAnimToggled(false)
        setIsFirstAnimToggled(false);
        setAnimationDirection(-1);
      }
      return;
    }

    const animateHint = () => {
      if (!enableInteractionHint) return;
      setShowInteractionHint(true);
      setHintAnimating(true);

      // Hand animation: left -> right -> center
      setTimeout(() => {
        const handElement = interactionHintRef.current;
        if (handElement) {
          gsap.timeline()
            .to(handElement, { x: -90, duration: 1, ease: "power2.inOut" })
            .to(handElement, { x: 90, duration: 1, ease: "power2.inOut" })
            .to(handElement, { x: 0, duration: 1, ease: "power2.inOut" });
        }
      }, 50);

      // Trigger model rotation animation in ThreeScene
      setIsObjectPanning(true);
      setTimeout(() => setIsObjectPanning(false), 3000);

      // Hide hint after animation using GSAP callback
      setTimeout(() => {
        setHintAnimating(false);
        setShowInteractionHint(false);
      }, 3000);
    };

    // Start animation after initial delay
    const initialDelay = setTimeout(animateHint, 3000);

    // Repeat animation every 8 seconds
    const repeatInterval = setInterval(animateHint, 8000);

    return () => {
      clearTimeout(initialDelay);
      clearInterval(repeatInterval);
      gsap.killTweensOf(interactionHintRef.current);
    };
  }, [gltfScene, enableInteractionHint, modelviewerSelected]);

  useEffect(() => {
    console.log('model viewer selected, 3D:', modelviewerSelected, modelviewerSelected === SelectedViewer.ThreeD);
  }, [modelviewerSelected]);

  console.log('isMobile:', isMobile, 'isAR:', isAR, 'showLabels:', showLabels);
  console.log('parent width modelviewer:', parentWidth);


  console.log('animation check:', !productSettings.animationMapping )

  // console.log('dimensions check:', objectDimensions !== undefined, objectDimensions !== "");
  
  //Reality converter on mac (glb to usdz)
  return (
    <>
      {/* Spinner Overlay (Only shows when loadingLightMap is true) */}
      {loadingLightMap && (
        <div
          ref={spinnerContainerRef}
          className="app-loading"
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            
            zIndex: 9999,
            height: '250px',
            width: '250px',
          }}
        />
      )}
      <div className="viewer-wrapper">
        <div className="touch-target">
          <div className="model-viewer" style={{ position: 'relative' }}>
            <Modal
              isOpen={modalIsOpen}
              onAfterOpen={afterOpenModal}
              onRequestClose={closeModal}
              style={customStyles}
              contentLabel="QR Code Modal"
            >
              <div className="modal-content">
                <button onClick={closeModal} className="close-button-qr">
                  <img src={`${window.ASSET_BASE_URL || process.env.PUBLIC_URL}/close.jpeg`} className="icon-qr"></img>
                </button>
                <div className="qr-code-text">
                  Scan to view in AR
                  <div className="qr-code-container">
                    <img src={qrCodeUrl} className="qr-code" alt="Scan to view in AR" />
                  </div>
                </div>
              </div>
            </Modal>
            <div className="loading-text" style={{ width: parentWidth, height: parentHeight, visibility: loadingLightMap ? 'hidden' : 'visible' }}>
              <Suspense fallback={<div>Loading...</div>}>
                <div ref={containerRef} style={{ position: "relative", width: "100%", height: "100%" }}>
                    <div 
                      className="canvas-wrapper"
                      style={{ 
                        position: "relative", 
                        width: "100%", 
                        height: "100%",
                        touchAction: isTouchDevice ? 'pan-y' : 'auto',
                        pointerEvents: isTouchDevice ? 'auto' : 'none'
                      }}
                    >
                      <Canvas
                        ref={canvasRef}
                        //Check for animation
                        frameloop={showLabels || animationPlaying || !orbitControlsEnabled || isObjectPanning || !isRotationSettled ? "always" : "demand"}
                        //frameloop="always"
                        //frameloop={isBaking ? "demand" : "always"}
                        shadows
                        // receiveshadow="true"
                        camera={{
                          position: [-2, 1, 2],
                          fov: 35,
                        }}
                        style={{ 
                          touchAction: isTouchDevice ? 'none' : 'auto', 
                          pointerEvents: isTouchDevice ? 'none' : 'auto' 
                        }}
                      >
                    {/* <Perf deepAnalyze={true} overClock={true} position='bottom-left' /> */}
                    {isAR ? (
                      <>
                        <XR store={store}>
                          <ARModel modelUrl={params.current.modelName + '.glb'} />
                          <Environment preset="sunset" />
                        </XR>
                      </>
                    ) : (
                      <>
                        {modelviewerSelected === SelectedViewer.ThreeD &&
                            <ThreeScene
                              //key={forceUpdate}
                              canvasRef={canvasRef}
                              controls={controls}
                              params={params.current}
                              cameraOffset={cameraOffset}
                              setCamTransform={setCamTransform}
                              camTransform={camTransform}
                              line_width={line_width}
                              showLabels={showLabels}
                              animationPlaying={animationPlaying}
                              setAnimationPlaying={setAnimationPlaying}
                              animationDirection={animationDirection}
                              lastAnimationName={lastAnimationName}
                              setLastAnimationName={setLastAnimationName}
                              isFirstAnimToggled={isFirstAnimToggled}
                              isSecondAnimToggled={isSecondAnimToggled}
                              setIsFirstAnimToggled={setIsFirstAnimToggled}
                              setIsSecondAnimToggled={setIsSecondAnimToggled}
                              selectedMeshName={selectedMeshName}
                              setSelectedMeshName={setSelectedMeshName}
                              setHasAnimations={setHasAnimations}
                              setAnimationLength={setAnimationLength}
                              setAnimationsLoaded={setAnimationsLoaded}
                              setGltfScene={setGltfScene}
                              isObjectPanning={isObjectPanning}
                              variantFileGlb={variantFileGlb}
                              variantFileUsd={variantFileUsd}
                              variantFilesHdr={variantFilesHdr}
                              variantEnvironmentsHdr={variantEnvironmentsHdr || `${process.env.PUBLIC_URL}/growm_studio.hdr`}
                              forceUpdate={forceUpdate}
                              yawAngle={yawAngle}
                              pitchAngle={pitchAngle}
                              windowWidth={windowWidth}
                              windowHeight={windowHeight}
                              aspectRatio={aspectRatio}
                              objectDimensions={objectDimensions}
                              parentWidth={parentWidth}
                              parentHeight={parentWidth}
                              sceneBackgroundColour={productSettings.sceneBackgroundColour}
                              isSmallObject={productTags.includes("smallObject")}
                              dimensionsOnTop={productTags.includes("dimensionsOnTop")}
                              productSettings={productSettings}
                              configuratorEnabled={configuratorEnabled}
                              isMobile={(isAndroid || isIOS || isMobile)}
                              onMeshClicked={(meshName) => {
                                console.log('Mesh clicked in ModelViewer:', meshName);
                                if (onMeshClicked) onMeshClicked(meshName);
                                toggleNamedAnimation(meshName);
                              }}
                              setLoadingLightMap={setLoadingLightMap}
                              setIsObjectPanning={setIsObjectPanning}
                              setEnableInteractionHint={setEnableInteractionHint}
                              enableInteractionHint={enableInteractionHint}
                              setIsRotationSettled={setIsRotationSettled}
                              isRotationSettled={isRotationSettled}
                              showInteractionHint={showInteractionHint}
                              viewerModeRef={viewerModeRef}
                              setDisableLabels={setDisableLabels}
                              controlsEnabled={controlsEnabled}
                              orbitControlsEnabled={orbitControlsEnabled}
                              setOrbitControlsEnabled={setOrbitControlsEnabled}
                              style={{
                                width: parentWidth,
                                height: parentHeight,
                              }}
                              isTouchDevice={isTouchDevice}
                            />
                        }
                        {/* <ScrollHandler />*/}
                        {/* <SmoothControlsUpdater /> */}
                        {/* This doesn't work because we need to sample shadows first. Can we trigger an event when 
                      soft shadows have settled? Using 70% GPU right now */}

                        {/* <BakeShadows ref={bakeShadowsRef} />
                      <SoftShadows ref={softShadowsRef} samples={16} size={10}/> */}
                        {/* <ShadowHandler isBaked={true} setIsBakingParent={setIsBaking}/> */}
                        {productTags.includes("jewellery") ? (
                          <Caustics color="#FF8F20" position={[0, -0.5, 0]} lightSource={[5, 5, -10]} worldRadius={0.01} ior={1.2} intensity={0.005}>
                            <mesh position={[-2, 0.5, -1]} scale={0.5}>
                              <MeshTransmissionMaterial resolution={1024} distortion={0.25} color="#FF8F20" thickness={1} anisotropy={1} />
                            </mesh>
                          </Caustics>
                        ) : null}
                        <EffectComposer>
                          <ToneMapping mode={ToneMappingMode.REINHARD2_ADAPTIVE} exposure={1} blendFunction={BlendFunction.ADD} />
                          <BrightnessContrast contrast={0.35} />
                          {productTags.includes("jewellery") ? (
                            <>
                              <Bloom
                                intensity={1.5}
                                luminanceThreshold={1}
                              />
                              <ChromaticAberration offset={[0.001, 0.001]} />
                            </>
                          ) : null}
                          {/* {!isAndroid ?
                          <N8AO
                            intensity={10}
                            radius={0.01}
                            distanceFalloff={0.5}
                            depthAwareUpsampling={true}
                            bias={0.2}
                            strength={20}
                            dark={1.5}
                          />
                          : null} */}
                        </EffectComposer>
                        {(!showLabels && !productTags.includes("smallObject") && !productSettings.isBaked ?
                          (!isAndroid) ?
                            <AccumulativeShadows
                              temporal
                              frames={100}
                              scale={5}
                              opacity={1}
                              resolution={2048}
                            >
                              <RandomizedLight amount={2} position={[0, 1, 0]} radius={5} />
                            </AccumulativeShadows>
                            : <ContactShadows
                              width={1}
                              height={1}
                              far={4}
                              scale={5}
                              opacity={0.65}
                              rotation={[Math.PI / 2, 0, 0]}
                              resolution={1024}
                              frames={1}
                            />
                          : null
                        )}
                      </>
                    )}
                  </Canvas>
                  {showInteractionHint && enableInteractionHint && (
                    <div
                      ref={interactionHintRef}
                      className={`interaction-hint ${hintAnimating ? 'animating' : ''}`}
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 1001,
                        pointerEvents: 'none',
                        opacity: 1,
                        fontSize: '48px'
                      }}
                    >
                      <img src={`${window.ASSET_BASE_URL || process.env.PUBLIC_URL}/icon_handtouch_black.svg`}
                        alt="Finger interaction"
                        style={{ width: 50, height: 50 }}
                      />
                    </div>
                  )}
                  </div>
                </div>
              </Suspense>
            </div>

            {/* Hide the button when not on a mobile device or on ios + show QR code. 
            TODO: Add this at higher level so we can show AR even if we don't show the scene in model viewer
            Button in Main will have to call a function that references a parameter in another component so it can trigger the scene (triggerAR for Android). 
            triggerAR and ios equivalent would need to be moved to another component entirely. activateAR isn't working!*/}
            {/* console.log('buttons check:', productSettings.showDimensions === true, (!productSettings.advancedDimensions && !productSettings.simpleDimensions),
            (productSettings.advancedDimensions || productSettings.simpleDimensions), (objectDimensions !== undefined && objectDimensions !== ""), hasAnimations); */}
            {
              (viewerMode !== "360" || !productSettings.ThreeSixtyViewToggle) && 
              (modelviewerSelected === SelectedViewer.ThreeD || (modelviewerSelected === SelectedViewer.ThreeSixty && productSettings.pure3D)) &&
              <div className="info-button-wrapper"
                style={{
                  position: 'absolute',
                  top: `${infoButtonWrapperTopMargin}px`,
                  left: `${infoButtonWrapperLeftMargin}px`,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  zIndex: 10
                }}
              >
                {/* Hide that button if we only have one animation or just dimensions to show. Need to check for animations and dimensions at the same time */}
                {showOptions &&
                  <div className="tooltip-right">
                    <button
                      onClick={() => setShowInfoDropdown(!showInfoDropdown)}
                      className={`button-dim ${showInfoDropdown ? "active" : ""}`}
                      style={{
                        width: Math.min(parentWidth * 0.16, maxViewerButtonSize),
                        height: Math.min(parentWidth * 0.16, maxViewerButtonSize),
                        boxShadow: showInfoDropdown ? `0 0 10px ${productSettings.buttonShadowColour || 'grey'}` : `0 0 3px ${productSettings.buttonShadowColour || 'grey'}`,
                        borderRadius: Math.min(parentWidth * 0.04, 25)
                      }}>
                      <img
                        src={`${window.ASSET_BASE_URL || process.env.PUBLIC_URL}/icon_options.svg`}
                        alt="Info Icon"
                        className="growmIcon"
                        style={{
                          width: Math.min(parentWidth * 0.08, maxViewerButtonSize / 2),
                          height: Math.min(parentWidth * 0.08, maxViewerButtonSize / 2)
                        }}
                      />
                      {!isMobile && <span className="tooltiptext">Show Options</span>}
                    </button>
                  </div>
                }

                <div
                  ref={dropdownRef}
                  className="info-dropdown"
                  style={{
                    marginTop: `${infoDropDownTopMargin}px`,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                    transformOrigin: 'top center',
                    zIndex: 10,
                  }}>
                  {((productSettings.showDimensions === true && ((!productSettings.advancedDimensions && !productSettings.simpleDimensions)
                    || ((productSettings.advancedDimensions || productSettings.simpleDimensions) && objectDimensions !== undefined && objectDimensions !== ""))))
                    && (dropdownVisible || !showOptions) && animationsLoaded && (
                    <div className="tooltip-right">
                      <button
                        onClick={() => {
                          if (!productSettings.ThreeSixtyViewToggle && productSettings.ThreeSixtyViewer !== -1 && productSettings.ModelViewer !== -1) toggleViewer();
                          toggleDimensions();
                        }}
                        className={`button-dim ${showLabels ? "active" : ""}`}
                        disabled={disableLabels}
                        style={{
                          width: Math.min(parentWidth * 0.16, maxViewerButtonSize),
                          height: Math.min(parentWidth * 0.16, maxViewerButtonSize),
                          boxShadow: showLabels ? `0 0 10px ${productSettings.buttonShadowColour || 'grey'}` : `0 0 3px ${productSettings.buttonShadowColour || 'grey'}`,
                          borderRadius: Math.min(parentWidth * 0.04, 25)
                        }}>
                        <img
                          src={`${window.ASSET_BASE_URL || process.env.PUBLIC_URL}/icon_dimensions.svg`}
                          // src={`${window.ASSET_BASE_URL || process.env.PUBLIC_URL}/icon_dimensions1.svg`}
                          alt="Dimensions Icon"
                          className="growmIcon"
                          style={{
                            width: Math.min(parentWidth * 0.08, maxViewerButtonSize / 2),
                            height: Math.min(parentWidth * 0.08, maxViewerButtonSize / 2)
                          }}
                        />
                        {!isMobile && <span className="tooltiptext">Show Object Dimensions</span>}
                      </button>
                    </div>
                  )}
                  {(dropdownVisible || !showOptions) && hasAnimations && (
                    <div className="tooltip-right" style={{ zIndex: 10 }}>
                      <button
                        onClick={toggleAnimation}
                        className={`button-anim-one ${hasAnimations ? '' : 'button disabled'} ${!animationPlaying ? 'active' : ''}`}
                        disabled={!hasAnimations}
                        style={{
                          width: Math.min(parentWidth * 0.16, maxViewerButtonSize),
                          height: Math.min(parentWidth * 0.16, maxViewerButtonSize),
                          boxShadow: isFirstAnimToggled ? `0 0 10px ${productSettings.buttonShadowColour || 'grey'}` : `0 0 3px ${productSettings.buttonShadowColour || 'grey'}`,
                          borderRadius: Math.min(parentWidth * 0.04, 25)
                        }}
                      >
                        <img
                          src={`${window.ASSET_BASE_URL || process.env.PUBLIC_URL}/icon_layers.svg`}
                          // src={`${window.ASSET_BASE_URL || process.env.PUBLIC_URL}/icon_layers.svg`}
                          alt="Animations Icon"
                          className="growmIcon"
                          style={{
                            width: Math.min(parentWidth * 0.08, maxViewerButtonSize / 2),
                            height: Math.min(parentWidth * 0.08, maxViewerButtonSize / 2)
                          }}
                        />
                        {!isMobile && <span className="tooltiptext">{productSettings.animButtonTooltip || "Play Animation"}</span>}
                      </button>
                    </div>
                  )}
                  {(dropdownVisible || !showOptions) && hasAnimations && animationLength >= 2 &&
                  productSettings.animationMapping && Object.keys(productSettings.animationMapping).length >= 2 && (
                    <div className="tooltip-right" style={{ zIndex: 10 }}>
                      <button
                        onClick={() => {
                          setSelectedMeshName("mechanism1");
                          onMeshClicked("mechanism1");
                          toggleNamedAnimation("mechanism1");
                        }}
                        className={`button-anim-two ${hasAnimations ? '' : 'button disabled'} ${!animationPlaying ? 'active' : ''}`}
                        disabled={!hasAnimations || animationLength < 2 || !productSettings.animationMapping || Object.keys(productSettings.animationMapping).length < 2}
                        style={{
                          width: Math.min(parentWidth * 0.16, maxViewerButtonSize),
                          height: Math.min(parentWidth * 0.16, maxViewerButtonSize),
                          boxShadow: isSecondAnimToggled ? `0 0 10px ${productSettings.buttonShadowColour || 'grey'}` : `0 0 3px ${productSettings.buttonShadowColour || 'grey'}`,
                          borderRadius: Math.min(parentWidth * 0.04, 25)
                        }}
                      >
                        <img
                          src={`${window.ASSET_BASE_URL || process.env.PUBLIC_URL}/icon_octopod_openclose.svg`}
                          // src={`${window.ASSET_BASE_URL || process.env.PUBLIC_URL}/icon_layers.svg`}
                          alt="Animations Icon"
                          className="growmIcon"
                          style={{
                            width: Math.min(parentWidth * 0.08, maxViewerButtonSize / 2),
                            height: Math.min(parentWidth * 0.08, maxViewerButtonSize / 2)
                          }}
                        />
                        {!isMobile && <span className="tooltiptext">{productSettings.animButtonTooltip2 || "Activate clasp"}</span>}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            }

            {(productSettings.ThreeSixtyViewToggle === undefined || productSettings.ThreeSixtyViewToggle === true) && 
            (modelviewerSelected === SelectedViewer.ThreeD || (modelviewerSelected === SelectedViewer.ThreeSixty && productSettings.pure3D)) && <div
              className="360-mode-button-wrapper"
              style={{
                position: 'absolute',
                bottom: '20px',
                left: '0px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                zIndex: 100
              }}>
              {
                (productSettings.pure3D) ? (
                  <div className="tooltip-right">
                    <button
                      onClick={toggleViewer}
                      className={`button-dim ${viewerMode === '3D' ? "active" : ""}`}
                      //disabled={disable3DSwitch}
                      style={{
                        width: Math.min(parentWidth * 0.16, maxViewerButtonSize),
                        height: Math.min(parentWidth * 0.16, maxViewerButtonSize),
                        boxShadow: viewerMode === '3D' ? `0 0 10px ${productSettings.buttonShadowColour || 'grey'}` : `0 0 3px ${productSettings.buttonShadowColour || 'grey'}`,
                        borderRadius: Math.min(parentWidth * 0.04, 25)
                      }}>
                      <img
                        src={`${window.ASSET_BASE_URL || process.env.PUBLIC_URL}/rotate360.png`}  // Path to the image in the public folder
                        alt="3D control Icon"
                        className="growmIcon"
                        style={{
                          width: Math.min(parentWidth * 0.08, maxViewerButtonSize / 2),
                          height: Math.min(parentWidth * 0.08, maxViewerButtonSize / 2)
                        }}
                      //style={{ filter: showLabels ? 'invert(100%)' : 'none' }}
                      />
                      {!isMobile && <span className="tooltiptext">Change control mode</span>}
                    </button>
                  </div>
                ) : null
              }
            </div>
            }

            {/* Hide the button when not on a mobile device or on Android + show QR code. Add #allowsContentScaling=0*/}
            <div
              className="ar-button-wrapper"
              style={{
                position: 'absolute',
                top: '20px',
                right: '0px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                zIndex: 10
              }}>
            {
              loading ? null : isAndroid &&
                !isIOS &&
                productSettings.isAREnabled ? null : !isIOS && // </> //   </button> //     /> //       className="icon" //       alt="View in AR Icon" //       src={`${process.env.PUBLIC_URL}/icon_viewinar.svg`}  // Path to the image in the public folder //     <img // <button onClick={triggerAR} className="ar-button"> // <>
                  productSettings.qrCode ? (
                !qrCodeUrl ? (
                  <p>Loading QR Code...</p>
                ) : (
                  <>
                    <div className="tooltip-left">
                      <button
                        onClick={openModal}
                        className={`ar-button`}
                        style={{
                          width: Math.min(parentWidth * 0.16, maxViewerButtonSize),
                          height: Math.min(parentWidth * 0.16, maxViewerButtonSize),
                          boxShadow: `0 0 3px ${productSettings.buttonShadowColour || 'grey'}`,
                          borderRadius: Math.min(parentWidth * 0.04, 25)
                        }}>
                        <img
                          src={`${window.ASSET_BASE_URL || process.env.PUBLIC_URL}/icon_viewinar.svg`}
                          alt="View in AR"
                          style={{
                            width: Math.min(parentWidth * 0.08, maxViewerButtonSize / 2),
                            height: Math.min(parentWidth * 0.08, maxViewerButtonSize / 2)
                          }}
                        />
                        {!isMobile && <span className="tooltiptext">Open in AR</span>}
                      </button>

                    </div>
                  </>
                )
              ) : null
            }
            {
              (productSettings.isAREnabled) ?
                isIOS ?
                  (
                    <a
                      rel="ar"
                      href={`${variantFileUsd}#allowsContentScaling=0`}
                      title="Open in AR"
                      className={`ar-button`}
                      style={{
                        width: Math.min(parentWidth * 0.16, maxViewerButtonSize),
                        height: Math.min(parentWidth * 0.16, maxViewerButtonSize),
                        boxShadow: `0 0 3px ${productSettings.buttonShadowColour || 'grey'}`,
                        borderRadius: Math.min(parentWidth * 0.04, 25)
                      }}>
                      <img src={`${window.ASSET_BASE_URL || process.env.PUBLIC_URL}/icon_viewinar.svg`} alt="View in AR" />
                    </a>
                  )
                  : (isAndroid) ? (
                    <>
                      <Suspense fallback={<div className='ar-loading-wrapper'>Loading AR environment...</div>}></Suspense>
                      <a
                        //Disable scaling here as well
                        href={`intent://arvr.google.com/scene-viewer/1.0?file=${variantFileGlbUncompressed}&mode=ar_preferred&title=My+Model&link=https://demo.growm.io#Intent;scheme=https;package=com.google.ar.core;end`}
                        title="Open in AR"
                        className={`ar-button`}
                        style={{
                          width: Math.min(parentWidth * 0.16, maxViewerButtonSize),
                          height: Math.min(parentWidth * 0.16, maxViewerButtonSize),
                          boxShadow: `0 0 3px ${productSettings.buttonShadowColour || 'grey'}`,
                          borderRadius: Math.min(parentWidth * 0.04, 25)
                        }}
                      >
                        <img src={`${window.ASSET_BASE_URL || process.env.PUBLIC_URL}/icon_viewinar.svg`}
                          alt="View in AR" />
                      </a>
                    </>
                  ) : null
                : null
              }
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ModelViewer;
